/* Agents view.
 *
 * Tile grid of every agent the firm has. An agent is an autonomous
 * executor that interprets intents and orchestrates skills with
 * judgment, within governance constraints — sitting on top of the
 * eight OrgRepo elements.
 *
 * Each tile: kind chip, name, on/off toggle, status, last-run.
 * Click → modal with the full editable detail + run history +
 * Run-now / Apply / Dismiss controls. The current daily-reflection
 * agents (function / service / industry / client / user) appear here
 * alongside any future ones. The Intent Router agent migration lands
 * in a follow-up.
 */
import React from 'react';
import { Icon, ThumbsRow, Avatar } from './primitives.jsx';
import { useAutoSave, SaveStatus } from '../lib/use-auto-save.jsx';
import { cronFromNatural, describeCron } from '../lib/schedule.js';
import { AgentRunInline, RunDetailRoute } from './agent-run-board.jsx';
import { DeckView } from './deck-view.jsx';
import { listAgents, getAgent, patchAgent, runAgentNow, applyAgentRun, dismissAgentRun, createAgent, deleteAgent, deleteAgentRun, clearAgentRuns, listPeople, uploadProposalIntakeFile, deleteProposalIntakeFile, regenerateAgentImage, uploadAgentImage, deleteAgentImage, markAgentRunSeen, getRunScope, setRunScope } from '../api.js';
import { useSubCrumbs } from './route-title-context.jsx';

/* ─── Run scope ("My runs" / "All runs") ──────────────────────────
 * One firm-wide preference (persisted in api.js) that governs every
 * run listing — the grid tiles' last-run snapshot, each agent's run
 * history, and the recent-runs timeline. Default "My runs"; admins can
 * flip to "All runs" for firm-wide oversight. Subscribing here means a
 * flip in any view re-renders all of them so they refetch in step. */
function useRunScope() {
  const [scope, setScope] = React.useState(getRunScope());
  React.useEffect(() => {
    const onChange = (e) => setScope(e?.detail || getRunScope());
    window.addEventListener('eve:runscope', onChange);
    return () => window.removeEventListener('eve:runscope', onChange);
  }, []);
  return scope;
}

// Admin-only segmented control. Reuses the category-chip styling so it
// sits naturally beside the tabs in the Agents header.
const RunScopeToggle = ({ scope }) => (
  <div role="group" aria-label="Run scope" style={{display:'inline-flex',gap:6,marginLeft:'auto'}}>
    {[['mine', 'My runs'], ['all', 'All runs']].map(([v, label]) => (
      <span
        key={v}
        className={`tag ${scope === v ? 'tag--orange' : ''}`}
        style={{cursor:'pointer'}}
        onClick={() => setRunScope(v)}
        title={v === 'all' ? 'Show every user’s runs (admin oversight)' : 'Show only your own runs'}
      >
        {label}
      </span>
    ))}
  </div>
);

// Agents that need a structured intake form before Run-now fires.
// Maps agent.id → intake-kind (selects which modal component to
// render). Today: the Proposal Builder seeded by migration 0037
// and the Redesign-in-Eraneos agent seeded by migration 0050.
const INTAKE_MODALS_BY_AGENT_ID = {
  'agent-proposal-builder':    'proposal',
  'agent-redesign-eraneos':    'redesign',
  'agent-slide-deck-creator':  'slidedeck',
  'agent-markdown-converter':  'markdown',
};

/* ─── Inline agent run viewer ─────────────────────────────────────
 * Custom agents create an agent_runs row when run-now fires; the run
 * goes through the planner + execution engine. This module parses
 * the run id out of the agent-run-history summary and hands off to
 * <AgentRunInline> (in agent-run-board.jsx) for the polling + step
 * board UI. */

const RUN_ID_RE = /\bSpawned run ([0-9a-f-]{36})\b/i;

function extractRunId(text) {
  if (!text) return null;
  const m = RUN_ID_RE.exec(text);
  return m ? m[1] : null;
}

// Spawned-run bookmark summaries are written as "Spawned run <uuid> — <title>"
// (see run-now.ts / skills.ts). Split that back into the human title and the
// raw id so the UI can lead with the intent and tuck the id underneath, rather
// than dumping the whole machine string on one line.
const SPAWN_SUMMARY_RE = /^Spawned run ([0-9a-f-]{36})(?:\s*—\s*([\s\S]*))?$/i;

function parseSpawnSummary(text) {
  if (!text) return null;
  const m = SPAWN_SUMMARY_RE.exec(text.trim());
  if (!m) return null;
  return { id: m[1], title: (m[2] || '').trim() };
}

/** Renders a spawned-run bookmark: the run's title (the intent) on top and the
 *  run id as a small grey sub-line. The title falls back to "Pending…" until
 *  the planner has named the run. Non-spawn summaries render as plain text so
 *  reflection/master runs are unaffected. `compact` keeps it to a single line
 *  (no id) for the dense earlier-runs list. */
const SpawnedRunSummary = ({ summary, fallback = '—', compact = false }) => {
  const parsed = parseSpawnSummary(summary);
  if (!parsed) return <>{summary || fallback}</>;
  const titleNode = parsed.title
    ? parsed.title
    : <span className="spawned-run__pending">Pending…</span>;
  if (compact) return titleNode;
  return (
    <span className="spawned-run">
      <span className="spawned-run__title">{titleNode}</span>
      <span className="spawned-run__id">{parsed.id}</span>
    </span>
  );
};

const KIND_META = {
  custom: { label: 'Custom', icon: 'sparkle', color: '#FF6428' },
  master: { label: 'Master', icon: 'compass', color: '#715AAE' },
  system: { label: 'System', icon: 'cog',     color: '#A20235' },
};
const STATUS_COLOR = {
  done:           { bg: '#DCF1E2',        fg: '#00702A' },
  failed:         { bg: '#FCE6E0',        fg: '#A20235' },
  'no-activity':  { bg: 'var(--bg-card)', fg: 'var(--fg-muted)' },
  running:        { bg: '#FFE7DC',        fg: '#B83C0F' },
  queued:         { bg: '#FFE7DC',        fg: '#B83C0F' },
  finished:       { bg: '#DCF1E2',        fg: '#00702A' },
  null:           { bg: 'var(--bg-card)', fg: 'var(--fg-muted)' },
};

// A run's call to action. Mirrors ActionState in
// functions/_lib/agent-run-attention.ts. Drives the "Action needed"
// marker + the inline CTA on collapsed run rows / tiles / timeline.
const ACTION_LABEL = { approve: 'Approve', rerun: 'Re-run', review: 'Open viewer' };
const ACTION_SHORT = { approve: 'Needs approval', rerun: 'Failed — re-run', review: 'Ready to review' };
// A run with no pending CTA stays collapsed once it's settled; an
// in-flight run keeps its live progress visible.
const RUN_IN_FLIGHT = (s) => s === 'running' || s === 'planning' || s === 'queued';

/** Does the scheduler run this agent unattended? Mirrors the backend
 *  `isScheduledAgent`. Falls back to deriving from trigger/cron when
 *  the API hasn't sent the computed `scheduled` flag (older cache). */
function isScheduledAgent(a) {
  if (typeof a?.scheduled === 'boolean') return a.scheduled;
  return a?.kind === 'master' || a?.triggerType === 'cron' || !!a?.scheduleCron;
}
/** The toggle's meaning depends on the agent type:
 *  scheduled → "Auto" (the unattended schedule on/off);
 *  manual    → "Available" (visible + runnable by people on/off). */
function toggleNoun(a) {
  return isScheduledAgent(a) ? 'Auto' : 'Available';
}

/**
 * AgentsView — the firm's single Agents page.
 *
 * Three tabs at the top:
 *   - System    — built-in master + system singletons. Admin-only.
 *                 Hidden from non-admins entirely.
 *   - Public    — admin-created custom agents (`scope='firm'`),
 *                 visible to every user.
 *   - Personal  — the viewer's own custom agents (`scope='personal'`),
 *                 invisible to anyone else.
 *
 * Non-admins land on Personal directly; admins land on the highest-
 * privilege tab they have content in (System if anything's there).
 *
 * Both legacy routes (`/agents` and `/personal-agents`) now render
 * this same component — the tab decides what's shown.
 */
export const AgentsView = ({ currentUser, subPath }) => {
  const isAdmin = currentUser?.role === 'admin';
  // Everyone lands on "All" — non-admins see master watchers + the public
  // agents shared with their role + their own personal agents; admins also
  // get the System tab. (Non-admins previously had only the Personal tab,
  // which hid every public agent from them.)
  const [tab, setTab] = React.useState('all');
  // Routed sub-path drives the whole right pane:
  //   <agentId>                      → agent overview
  //   <agentId>/<runId>              → run detail (inline, not a popup)
  //   <agentId>/<runId>/deck         → deck viewer (full-width)
  const [deepAgentId, deepRunId, deepLeaf] = (subPath || '').split('?')[0].split('/').filter(Boolean);
  return (
    <AgentsViewBase
      tab={tab}
      setTab={setTab}
      currentUser={currentUser}
      deepAgentId={deepAgentId || null}
      deepRunId={deepRunId || null}
      deepLeaf={deepLeaf || null}
    />
  );
};

/** Kept for back-compat with imports the rest of the app may have. */
export const PersonalAgentsView = AgentsView;

const TAB_META = {
  all:      { label: 'All Agents',     lede: 'Every agent you can see in one list — master watchers, system singletons, admin-shared public agents, and personal agents. Pick one to edit + run it, or scan the latest runs across all of them on the right.' },
  system:   { label: 'System Agents',  lede: 'Firm-wide built-ins: master agents (news watchers, Eve, morning briefings, feedback optimiser) plus system singletons (chat eval, agent-run eval, strategy cascade). Each runs on a cron, webhook, or agent trigger. Edit prompts inline; flip on/off; trigger Run-now. Admins can also add new manual-trigger system agents with the + button.' },
  public:   { label: 'Public Agents',  lede: 'Admin-authored custom agents available to every user — Proposal Builder, Slide-Deck Creator, Redesign in Eraneos, plus anything an admin has shared. Each user runs their own instance with their own intake.' },
  personal: { label: 'Personal Agents', lede: 'Custom agents you created for your own work. Only you see them. Use the + button to draft one.' },
};

/** Does an agent belong to the given selector tab? `all` is the union
 *  of everything the viewer can see (the /api/agents list is already
 *  visibility-filtered server-side). `system` rolls up master + system
 *  singletons; `public` is admin-shared custom agents; `personal` is
 *  the viewer's own (admins see everyone's). */
function agentMatchesTab(a, tab, isAdmin, userId) {
  switch (tab) {
    case 'system':   return a.kind === 'master' || a.kind === 'system';
    case 'public':   return a.kind === 'custom' && a.scope === 'firm';
    case 'personal': return a.kind === 'custom' && a.scope === 'personal' && (isAdmin || a.ownerUserId === userId);
    case 'all':
    default:         return true;
  }
}

/** Mirror of the backend `agentVisibleToRole` (functions/_lib/learning.ts):
 *  does a viewer with `role` fall inside a public agent's visibility?
 *  'all' / empty / NULL → everyone; otherwise a comma role list
 *  ('user,eOS-Manager') the role must appear in. Admins see everything.
 *  Kept in sync so the Run-now button matches what run-now.ts authorizes. */
function agentVisibleToRole(visibility, role) {
  if (role === 'admin') return true;
  const v = (visibility ?? 'all').trim();
  if (v === '' || v === 'all') return true;
  return v.split(',').map(s => s.trim()).filter(Boolean).includes(role);
}

/** Can this viewer spawn a run on `agent`? Mirrors run-now.ts: admins run
 *  anything; the owner of a personal agent runs their own; any viewer
 *  whose role is in a firm-scope ("public") agent's visibility runs it.
 *  Master + system agents stay admin-only. */
function canRunAgent(agent, isAdmin, userId, role) {
  if (!agent) return false;
  if (isAdmin) return true;
  if (agent.kind === 'custom' && agent.scope === 'personal') return agent.ownerUserId === userId;
  if (agent.kind === 'custom' && agent.scope === 'firm') return agentVisibleToRole(agent.visibility, role);
  return false;
}

const AgentsViewBase = ({ tab, setTab, currentUser, deepAgentId, deepRunId, deepLeaf }) => {
  // `raw` holds the full /api/agents response (all kinds + scopes).
  // The per-tab subset is derived below via useMemo so switching tabs
  // is a pure client-side filter — no network round-trip, no
  // ensureBuiltinAgents schema dance. The old behaviour re-fetched on
  // every tab click, which is what made switching feel slow.
  const [raw, setRaw] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [activeId, setActiveId] = React.useState(null);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);   // { id, name, promptTemplate }
  const [pickerOpen, setPickerOpen] = React.useState(false);   // "+ New run" agent picker
  const isAdmin = currentUser?.role === 'admin';
  // Navigate the agents view via the URL hash so selection, run detail and
  // the deck viewer are all bookmarkable and drive the breadcrumb.
  const goAgent = (agentId) => { location.hash = `agents/${encodeURIComponent(agentId)}`; };
  const goRoot  = () => { location.hash = 'agents'; };

  // The System tab (master + system singletons, with prompt-editing
  // controls) is admin-only. If a non-admin ever lands on it (URL
  // tampering, a stale tab), bounce them to the All view, which already
  // shows the master watchers they're allowed to see.
  React.useEffect(() => {
    if (!isAdmin && tab === 'system') setTab('all');
  }, [isAdmin, tab, setTab]);

  // Tab-driven "who can edit what" gate.
  // - Personal: the viewer owns every visible tile.
  // - Public:   only admins can create / edit / delete.
  // - System:   only admins can toggle / edit prompts.
  const canControl = isAdmin || tab === 'personal';

  // Default type for new agents in the current tab. Admin sees a
  // picker in the modal and can change it; non-admins are pinned to
  // 'personal' regardless of tab.
  const defaultAgentType = isAdmin
    ? (tab === 'system' ? 'system' : tab === 'public' ? 'public' : 'personal')
    : 'personal';

  // Firm-wide run scope. Refetch the agents feed whenever it flips so
  // the grid tiles' last-run snapshot + the timeline follow the toggle.
  const runScope = useRunScope();
  const reload = React.useCallback(async () => {
    setError(null);
    try {
      setRaw(await listAgents());
    } catch (err) {
      setError(err.message);
    }
  }, [runScope]);

  // Per-tab subset — derived, not fetched.
  const data = React.useMemo(() => {
    if (!raw) return null;
    const filtered = (raw.agents || []).filter(a => agentMatchesTab(a, tab, isAdmin, currentUser?.id));
    return { ...raw, agents: filtered };
  }, [raw, tab, isAdmin, currentUser?.id]);

  // Per-tab agent counts for the selector chips — derived from the
  // full list so every chip shows its tally regardless of which tab
  // is active.
  const counts = React.useMemo(() => {
    const list = raw?.agents || [];
    const by = t => list.filter(a => agentMatchesTab(a, t, isAdmin, currentUser?.id)).length;
    return { all: by('all'), system: by('system'), public: by('public'), personal: by('personal') };
  }, [raw, isAdmin, currentUser?.id]);

  // Latest runs across the agents in the active tab (no agent
  // selected). The /api/agents response carries a `recentRuns` feed
  // (last 36h); keep only runs whose agent is visible in this tab.
  const tabRuns = React.useMemo(() => {
    const ids = new Set((data?.agents || []).map(a => a.id));
    return (raw?.recentRuns || []).filter(r => ids.has(r.agentId));
  }, [raw, data]);

  // Breadcrumbs are owned by the leaf views (AgentDetail / RunDetailRoute /
  // DeckView) so the deepest open surface sets the full trail without the
  // parent overwriting it.
  const tabLabel = tab === 'all' ? 'All' : tab === 'system' ? 'System Agents' : tab === 'public' ? 'Public' : 'Personal';

  const onDeleteCustom = async a => {
    if (!window.confirm(`Delete custom agent "${a.targetLabel}"? Removes the agent and its run history. Spawned intents remain.`)) return;
    try {
      await deleteAgent(a.id);
      await reload();
    } catch (err) {
      setError(err.message);
    }
  };

  React.useEffect(() => { reload(); }, [reload]);

  // Selection is hash-driven: `activeId` mirrors the agent id in the URL
  // (`#agents/<agentId>[…]`). Clicking a tile / timeline row / picker sets
  // the hash (goAgent), which flows back here. Switch the admin tab to the
  // agent's category when deep-linked from elsewhere.
  React.useEffect(() => {
    setActiveId(deepAgentId || null);
    if (deepAgentId && raw && isAdmin) {
      const agent = (raw.agents || []).find(a => a.id === deepAgentId);
      if (agent) {
        const t = (agent.kind === 'master' || agent.kind === 'system')
          ? 'system'
          : agent.scope === 'firm' ? 'public' : 'personal';
        if (t !== tab) setTab(t);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepAgentId, raw, isAdmin]);

  const toggle = async a => {
    // Optimistic update: flip the tile locally, fire PATCH in the
    // background. Avoids the 1-2s /api/agents refetch (which runs the
    // entity backfill) on every on/off click. Roll back on error.
    setRaw(d => d ? {
      ...d,
      agents: (d.agents || []).map(x => x.id === a.id ? { ...x, enabled: !x.enabled } : x),
    } : d);
    try {
      await patchAgent(a.id, { enabled: !a.enabled });
    } catch (err) {
      setRaw(d => d ? {
        ...d,
        agents: (d.agents || []).map(x => x.id === a.id ? { ...x, enabled: a.enabled } : x),
      } : d);
      setError(err.message);
    }
  };

  if (error) return (
    <div className="page">
      <div style={{padding:16,background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:13,maxWidth:520}}>
        <div style={{marginBottom:10}}>Couldn’t load agents: {error}</div>
        <button
          type="button"
          className="tag tag--orange"
          style={{cursor:'pointer'}}
          onClick={() => { setError(null); reload(); }}
        >
          Retry
        </button>
      </div>
    </div>
  );
  if (!data) return <div className="page"><div style={{padding:24,color:'var(--fg-muted)',fontSize:13}}>Loading…</div></div>;

  // Deck viewer at #agents/<agentId>/<runId>/deck — rendered full-width
  // (its own editing surface); the breadcrumb carries the trail back.
  const deepAgentName = (raw?.agents || []).find(a => a.id === deepAgentId)?.targetLabel || null;
  if (deepAgentId && deepRunId && deepLeaf === 'deck') {
    // md-first decks (Slide creator 2) carry a `v2-…` id and render client-side
    // through the same viewer; everything else is an agent run.
    const isV2 = deepRunId.startsWith('v2-');
    return (
      <DeckView
        subPath={`${encodeURIComponent(deepRunId)}/deck?agent=${encodeURIComponent(deepAgentId)}`}
        mode={isV2 ? 'v2' : 'agent'}
        agentName={deepAgentName}
        runHref={`agents/${encodeURIComponent(deepAgentId)}/${encodeURIComponent(deepRunId)}`}
      />
    );
  }

  const meta = TAB_META[tab];
  const tabs = isAdmin
    ? [['all', 'All'], ['system', 'System'], ['public', 'Public'], ['personal', 'Personal']]
    : [['all', 'All'], ['public', 'Public'], ['personal', 'Personal']];

  return (
    <div className="page">
      <div className="page__head" style={{display:'flex',alignItems:'center',gap:14,flexWrap:'wrap'}}>
        <h1 style={{margin:0}}>Agents</h1>
        {/* New agent = the round "+" beside the title (canonical control;
            Refresh is handled by the global pull-to-refresh gesture). */}
        <button
          type="button"
          className="btn-round-add"
          onClick={() => setCreateOpen(true)}
          aria-label="New agent"
          title="New agent"
        >
          <Icon name="plus" size={18}/>
        </button>
      </div>
      {/* Category chips — filter both the agent selector and the timeline.
          The admin-only "My runs / All runs" scope toggle rides on the
          right of the same row so it governs every view from one place. */}
      {(tabs.length > 1 || isAdmin) && (
        <div style={{display:'flex',gap:6,marginBottom:14,flexWrap:'wrap',alignItems:'center'}}>
          {tabs.map(([id, label]) => (
            <span
              key={id}
              className={`tag ${tab === id ? 'tag--orange' : ''}`}
              style={{cursor:'pointer'}}
              onClick={() => { setTab(id); goRoot(); }}
            >
              {label} ({counts[id] ?? 0})
            </span>
          ))}
          {isAdmin && <RunScopeToggle scope={runScope}/>}
        </div>
      )}

      <div className="lede" style={{marginBottom:16}}>{meta.lede}</div>

      {data.agents.length === 0 && !activeId ? (
        <div style={{padding:24,background:'var(--bg-alt)',borderRadius:12,fontSize:13.5,color:'var(--fg-muted)',maxWidth:760}}>
          No agents in this tab yet. {tab === 'personal'
            ? <>Click the <strong>+</strong> button next to the heading to spin one up — give it a prompt, hit Run-now, and watch the plan + result land here.</>
            : tab === 'public'
              ? <>Admins create public agents with the <strong>+</strong> button next to the heading. They become visible to every user in the firm.</>
              : tab === 'system'
                ? <>Built-in system agents are seeded from migrations; admins can also add manual-trigger ones with the <strong>+</strong> button next to the heading.</>
                : <>Nothing to show yet. Use the <strong>+</strong> button next to the heading to create one.</>}
        </div>
      ) : (
        <div className="agents-layout">
          {/* Horizontal agent slider across the top — scrolls left→right.
              Stays visible whether the pane below shows the recent-runs
              timeline or a selected agent's detail. */}
          <div className="agents-slider">
            {/* Sort by most-recently-run first (no-run agents drift to
                the bottom). Within a row: status pill + relative time +
                kind tag, agent label below — same shape as the Intents
                list so the two views feel related. */}
            {[...data.agents]
              .sort((a, b) => (b.lastRunAt ? +new Date(b.lastRunAt) : 0) - (a.lastRunAt ? +new Date(a.lastRunAt) : 0))
              .map(a => {
                const meta = KIND_META[a.kind] || { label: a.kind, color: 'var(--fg-muted)' };
                const sc = STATUS_COLOR[a.lastStatus || 'null'];
                const next = nextRunHint(a);
                return (
                  <div
                    key={a.id}
                    className={`agent-tile-hero ${activeId === a.id ? 'on' : ''}`}
                    onClick={() => goAgent(a.id)}
                    style={{
                      position:'relative', display:'flex', flexDirection:'column', padding:0, borderRadius:12,
                      overflow:'hidden', height:128, cursor:'pointer', color:'#fff',
                      background:'#202020',  // flat black fallback when the agent has no image
                      boxShadow: activeId === a.id ? '0 0 0 2px var(--orange)' : 'none',
                      opacity: a.enabled ? 1 : 0.62,
                    }}
                  >
                    {a.imageUrl && (
                      <>
                        <div style={{position:'absolute',inset:0,backgroundImage:`url(${a.imageUrl}&w=768)`,backgroundSize:'cover',backgroundPosition:'center'}}/>
                        <div style={{position:'absolute',inset:0,background:'var(--orange)',opacity:0.1,mixBlendMode:'multiply'}}/>
                        <div style={{position:'absolute',inset:0,background:'linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.5) 42%, rgba(36,18,8,0) 100%)'}}/>
                      </>
                    )}
                    {/* kind-colour accent rail, kept from the old row for continuity */}
                    <div style={{position:'absolute',left:0,top:0,bottom:0,width:3,background:meta.color,zIndex:2}}/>
                    <div style={{position:'relative',zIndex:2,padding:'10px 13px',display:'flex',flexDirection:'column',justifyContent:'flex-end',flex:1,minHeight:0}}>
                      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:'auto'}}>
                        <span style={{fontSize:9.5,padding:'2px 7px',borderRadius:4,background:sc.bg,color:sc.fg,textTransform:'uppercase',letterSpacing:'0.04em',fontWeight:600,whiteSpace:'nowrap',flexShrink:0}}>
                          {a.lastStatus || 'never'}
                        </span>
                        {a.needsAttention && (
                          <span
                            title="A recent run needs your attention — open the agent to act"
                            style={{fontSize:9,padding:'2px 7px',borderRadius:999,background:'var(--orange)',color:'#fff',textTransform:'uppercase',letterSpacing:'0.04em',fontWeight:700,whiteSpace:'nowrap',flexShrink:0,boxShadow:'0 1px 4px rgba(0,0,0,0.28)'}}
                          >
                            {ACTION_SHORT[a.actionState] || 'Action needed'}
                          </span>
                        )}
                        <div style={{flex:1}}/>
                        <span style={{
                          display:'inline-flex',alignItems:'center',gap:5,
                          fontSize:10,fontWeight:600,letterSpacing:'0.04em',textTransform:'uppercase',
                          whiteSpace:'nowrap',flexShrink:0,
                          color: a.enabled ? '#fff' : 'rgba(255,255,255,0.55)',
                        }}>
                          <span style={{width:7,height:7,borderRadius:'50%',background: a.enabled ? 'var(--orange)' : 'rgba(255,255,255,0.45)'}}/>
                          {toggleNoun(a)} {a.enabled ? 'on' : 'off'}
                        </span>
                      </div>
                      {/* Kind label sits directly above the name, mirroring the
                          detail hero's cover treatment. */}
                      <span style={{fontSize:10,color:'var(--orange)',textTransform:'uppercase',letterSpacing:'0.06em',fontWeight:700,whiteSpace:'nowrap',marginTop:10,textShadow:'0 1px 3px rgba(0,0,0,0.6)'}}>{meta.label}</span>
                      <div style={{fontSize:14.5,lineHeight:1.2,fontWeight:600,letterSpacing:'-0.01em',marginTop:2,wordBreak:'break-word',textShadow:'0 1px 4px rgba(0,0,0,0.55)'}}>
                        {a.targetLabel}
                      </div>
                      <div style={{display:'flex',gap:8,fontSize:10.5,color:'rgba(255,255,255,0.78)',flexWrap:'wrap',marginTop:3}}>
                        <span>Last: {a.lastRunAt ? relTime(a.lastRunAt) : '—'}</span>
                        <span>· Next: {next}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>

          <div className="agents-detail">
            {/* Deselect → back to the full-width recent-runs timeline. */}
            {activeId && (
              <button
                type="button"
                className="agents-back-btn"
                onClick={goRoot}
                aria-label="Back to recent runs"
              >
                <Icon name="arrow-left" size={16}/> Back to recent runs
              </button>
            )}
            {deepAgentId && deepRunId
              ? (
                  // Run detail — inline in the full-width pane (not a popup).
                  <RunDetailRoute
                    agentId={deepAgentId}
                    runId={deepRunId}
                    agentName={deepAgentName}
                    onChanged={reload}
                  />
                )
              : activeId
              ? (() => {
                  // Per-agent control gate. Personal-scope custom
                  // agents → the owner OR a real admin can edit /
                  // delete / toggle. Firm-scope custom agents (e.g.
                  // the seeded Proposal Builder) appear in everyone's
                  // Personal Agents view so they can be run-by-all,
                  // but only a REAL admin should see the edit
                  // affordances on them. Deleting a PUBLIC or SYSTEM
                  // agent is done from Admin → Agents, not here — so
                  // onDelete below is wired only for personal agents.
                  const activeAgent = (data?.agents || []).find(a => a.id === activeId);
                  const personalOwnerCanControl = tab === 'personal'
                    && activeAgent?.kind === 'custom'
                    && activeAgent?.scope === 'personal'
                    && activeAgent?.ownerUserId === currentUser?.id;
                  const controlThisAgent = isAdmin || personalOwnerCanControl;
                  // Run-now is broader than admin control: any viewer the
                  // backend (run-now.ts) would authorize — owner of a
                  // personal agent, or a role inside a public agent's
                  // visibility ("For all" / eOS-Manager / user) — gets the
                  // button, even without edit/toggle/delete rights.
                  const canRunThisAgent = canRunAgent(activeAgent, isAdmin, currentUser?.id, currentUser?.role);
                  return (
                    <AgentDetail
                      key={activeId}
                      id={activeId}
                      initialAgent={activeAgent}
                      isAdmin={controlThisAgent}
                      canRun={canRunThisAgent}
                      onChanged={reload}
                      onEdit={agent => (agent.kind === 'custom' || agent.kind === 'master') && controlThisAgent
                        ? setEditing({
                            id: agent.id,
                            name: agent.targetLabel,
                            promptTemplate: agent.promptTemplate || agent.defaultPromptTemplate || '',
                            defaultPromptTemplate: agent.defaultPromptTemplate || '',
                            hasOverride: !!agent.promptTemplate,
                            kind: agent.kind,
                            imageUrl: agent.imageUrl || null,
                            imagePrompt: agent.imagePrompt || '',
                          })
                        : null}
                      onDelete={activeAgent?.kind === 'custom' && activeAgent?.scope === 'personal' && controlThisAgent
                        ? (agent => onDeleteCustom(agent))
                        : null}
                    />
                  );
                })()
              : <RunsTimeline
                  runs={tabRuns}
                  tabLabel={tabLabel}
                  onSelectAgent={goAgent}
                  onNewRun={() => setPickerOpen(true)}
                  onOpenRun={(agentId, runId) => { location.hash = `agents/${encodeURIComponent(agentId)}/${encodeURIComponent(runId)}`; }}
                />}
          </div>
        </div>
      )}

      {createOpen && (
        <AgentEditorModal
          mode="create"
          defaultType={defaultAgentType}
          isAdmin={isAdmin}
          onClose={() => setCreateOpen(false)}
          onSaved={async saved => {
            setCreateOpen(false);
            await reload();
            // Auto-select the new agent on the right so the user goes
            // straight into the detail view (and can hit Run-now to
            // watch the plan land).
            if (saved?.id) {
              goAgent(saved.id);
              // Auto-run immediately — saves the user a click and
              // shows the live step board the moment they finish
              // creating the agent. Run-now is fire-and-forget at
              // the API level; the polling RunBoard picks it up.
              try { await runAgentNow(saved.id); } catch { /* surface errors via the agent detail */ }
              await reload();
            }
          }}
        />
      )}

      {editing && (
        <AgentEditorModal
          key={editing.id}
          mode="edit"
          isAdmin={isAdmin}
          initial={editing}
          onClose={async () => { setEditing(null); await reload(); }}
          onSaved={async () => { setEditing(null); await reload(); }}
        />
      )}

      {/* "+ New run" → pick an agent (grouped by category), open it to run. */}
      {pickerOpen && (
        <AgentPickerModal
          agents={data.agents}
          onClose={() => setPickerOpen(false)}
          onPick={a => { setPickerOpen(false); goAgent(a.id); }}
        />
      )}

      {/* AgentDetail renders inline on the right pane (above). We
          don't open AgentDetailModal here — clicking an agent (or an
          Overnight updates row) just selects it on the right. */}
    </div>
  );
};

// Map a (kind, scope) tuple from the backend to a single user-facing
// type token, and back. The four logical types are:
//   - 'master'   → admin-managed firm-wide cron watcher (seeded only)
//   - 'system'   → admin-managed singleton, manual trigger
//   - 'public'   → admin-managed, visible to every user
//   - 'personal' → user-owned, private (admins can see all users')
const typeFromAgent = (a) => {
  if (!a) return 'personal';
  if (a.kind === 'master') return 'master';
  if (a.kind === 'system') return 'system';
  if (a.kind === 'custom' && a.scope === 'firm') return 'public';
  return 'personal';
};
const tupleFromType = (t) => {
  if (t === 'system') return { kind: 'system', scope: 'firm' };
  if (t === 'public') return { kind: 'custom', scope: 'firm' };
  // 'personal' (and 'master', though master isn't creatable from the
  // UI — guarded at the picker level)
  return { kind: 'custom', scope: 'personal' };
};

const AgentEditorModal = ({ mode, defaultType, isAdmin, initial, onClose, onSaved }) => {
  const [name, setName] = React.useState(initial?.name || '');
  const [promptTemplate, setPromptTemplate] = React.useState(initial?.promptTemplate || '');
  // For creates, the type is picked from the dialog (admin) or pinned
  // to 'personal' (non-admin). For edits, the type is derived from
  // the existing agent and locked — changing it would alter who can
  // see the agent mid-flight.
  const initialType = mode === 'edit'
    ? typeFromAgent(initial)
    : (isAdmin ? (defaultType || 'personal') : 'personal');
  const [pickedType, setPickedType] = React.useState(initialType);
  // Optional schedule. Admin-only, optional. Two separate fields: a
  // plain-English helper ("every weekday at 9am") that, on an explicit
  // Convert press, fills the cron field — and the cron field itself,
  // which is the stored value and is directly editable. Conversion is
  // manual on purpose (no live auto-update), so editing one field never
  // clobbers the other. Blank = "manual only — run via Run-now"; the
  // runtime tick honours the cron for custom + admin-created system agents.
  const [scheduleText, setScheduleText] = React.useState('');
  const [scheduleCron, setScheduleCron] = React.useState(initial?.scheduleCron || '');
  const [scheduleErr, setScheduleErr] = React.useState(null);
  // Read-only echo: what the cron in the field means, in plain English.
  const cronDesc = React.useMemo(() => {
    const c = scheduleCron.trim();
    if (!c) return null;
    try { return describeCron(c); } catch { return null; }
  }, [scheduleCron]);
  const cronValid = !scheduleCron.trim() || cronDesc !== null;
  const convertSchedule = () => {
    if (!scheduleText.trim()) { setScheduleErr(null); return; }
    const r = cronFromNatural(scheduleText);
    if (r.cron) { setScheduleCron(r.cron); setScheduleErr(null); }
    else setScheduleErr(r.error || 'Couldn’t read that as a schedule.');
  };
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const isCreate = mode === 'create';
  // Master agents: name is hardcoded in MASTER_AGENTS, prompt is the
  // editable "lens" that overrides the module's DEFAULT_*_LENS.
  // Empty prompt means "use the default" — so we don't require it.
  const isMaster = initial?.kind === 'master';

  // Agent card image — generate / regenerate / upload / delete, available
  // when editing an existing agent (creates auto-generate one server-side).
  const [imgUrl, setImgUrl] = React.useState(initial?.imageUrl || null);
  const [imgPrompt, setImgPrompt] = React.useState(initial?.imagePrompt || '');
  const [imgBusy, setImgBusy] = React.useState(false);
  const [imgErr, setImgErr] = React.useState(null);
  const imgFileRef = React.useRef(null);
  const regenImg = async () => {
    setImgBusy(true); setImgErr(null);
    try {
      const a = await regenerateAgentImage(initial.id, imgPrompt);
      setImgUrl(a?.imageUrl || null);
      // Reflect the prompt actually used (e.g. the one Claude authored when
      // the field was left blank) so the user can tweak it further.
      if (a?.imagePrompt) setImgPrompt(a.imagePrompt);
    }
    catch (e) { setImgErr(e.message || String(e)); }
    finally { setImgBusy(false); }
  };
  const uploadImg = async (f) => {
    if (!f) return;
    setImgBusy(true); setImgErr(null);
    try { const a = await uploadAgentImage(initial.id, f); setImgUrl(a?.imageUrl || null); }
    catch (e) { setImgErr(e.message || String(e)); }
    finally { setImgBusy(false); }
  };
  const deleteImg = async () => {
    if (!window.confirm('Remove this agent\'s image? It will show a plain black card.')) return;
    setImgBusy(true); setImgErr(null);
    try { const a = await deleteAgentImage(initial.id); setImgUrl(a?.imageUrl || null); }
    catch (e) { setImgErr(e.message || String(e)); }
    finally { setImgBusy(false); }
  };

  // Reseed form state whenever a different agent is opened, OR the
  // same agent's prompt was updated externally. useState initialisers
  // only fire on mount — without this effect, switching from agent A
  // to agent B would leave the form showing A's prompt.
  // Internal baseline of the last-persisted values, so edit-mode auto-save
  // can tell dirtiness without depending on the `initial` prop (which the
  // parent only refreshes when the modal closes).
  const [baselineSig, setBaselineSig] = React.useState(null);
  React.useEffect(() => {
    setName(initial?.name || '');
    setPromptTemplate(initial?.promptTemplate || '');
    setError(null);
    setBaselineSig(JSON.stringify({ name: (initial?.name || '').trim(), prompt: (initial?.promptTemplate || '').trim() }));
  }, [initial?.id, initial?.name, initial?.promptTemplate]);

  const currentSig = JSON.stringify({ name: name.trim(), prompt: promptTemplate.trim() });
  const dirty = !isCreate && baselineSig !== null && currentSig !== baselineSig;

  // Edit-mode persist for auto-save: patch in place without closing.
  const persist = async () => {
    if (!isMaster && (!name.trim() || !promptTemplate.trim())) throw new Error('Name and prompt are required');
    if (isMaster) {
      // Save unchanged-from-default (or cleared) → drop the override so the
      // agent keeps tracking firm-wide default updates.
      const trimmed = promptTemplate.trim();
      const def = (initial?.defaultPromptTemplate || '').trim();
      const next = (def && trimmed === def) ? '' : trimmed;
      await patchAgent(initial.id, { promptTemplate: next });
    } else {
      await patchAgent(initial.id, { name: name.trim(), promptTemplate: promptTemplate.trim() });
    }
    setBaselineSig(currentSig);
  };

  const { status: saveStatus, error: saveError, saveNow } = useAutoSave({
    dirty,
    save: persist,
    enabled: !isCreate,
    signature: currentSig,
  });

  const submit = async e => {
    e.preventDefault();
    if (!isCreate) { saveNow(); return; }
    if (!isMaster && (!name.trim() || !promptTemplate.trim())) return;
    if (scheduleCron.trim() && !cronValid) {
      setError('The schedule isn’t a valid 5-field cron. Fix it, or type a description and press Convert.');
      return;
    }
    setBusy(true); setError(null);
    try {
      const { kind, scope } = tupleFromType(pickedType);
      const saved = await createAgent({
        name: name.trim(),
        promptTemplate: promptTemplate.trim(),
        scope,
        kind: kind === 'system' ? 'system' : undefined,
        scheduleCron: scheduleCron.trim() || null,
      });
      onSaved?.(saved);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <div className="modal-mask" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{position:'relative',maxWidth:640,width:'95%'}}>
        <button className="modal__close" onClick={onClose}>×</button>
        <div style={{fontSize:11,letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--orange)',marginBottom:6,fontWeight:600}}>
          {isCreate
            ? `New ${pickedType} agent`
            : (isMaster ? 'Edit master agent prompt' : 'Edit agent')}
        </div>
        <h3 style={{marginBottom:6}}>{isCreate ? 'Describe what this agent should do' : `Editing: ${initial?.name}`}</h3>
        <p style={{color:'var(--fg-muted)',fontSize:12.5,marginBottom:14,lineHeight:1.5}}>
          {isMaster
            ? <>This is the <em>search lens</em> the agent uses on every entity it iterates over. The textarea is pre-filled with the {initial?.hasOverride ? <strong>override you saved previously</strong> : <strong>firm-wide default</strong>} — edit to refine it. Save unchanged (or clear) to keep tracking firm-wide updates to the default lens automatically.</>
            : <>The agent runs by spawning an intent with its <em>prompt template</em> as the description. The Intent Router then plans + executes the steps. Run-now triggers a fresh run; the run history shows the intents this agent has spawned.</>}
        </p>

        {error && <div style={{margin:'0 0 12px',padding:10,background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:13}}>{error}</div>}

        <form onSubmit={submit}>
          {!isMaster && (
            <div className="field">
              <label>Name</label>
              <input className="input" value={name} onChange={e => setName(e.target.value)} maxLength={200} placeholder="e.g. Friday EPH prep"/>
            </div>
          )}

          {/* Card image — generate on-brand with AI, upload your own, or
              delete (falls back to a plain black card). Edit-mode only. */}
          {!isCreate && initial?.id && (
            <div className="field">
              <label>Card image</label>
              <div style={{position:'relative',borderRadius:10,overflow:'hidden',height:120,background:'#202020',color:'#fff',display:'flex',alignItems:'flex-end',marginBottom:8}}>
                {imgUrl && (
                  <>
                    <div style={{position:'absolute',inset:0,backgroundImage:`url(${imgUrl}&w=768)`,backgroundSize:'cover',backgroundPosition:'center'}}/>
                    <div style={{position:'absolute',inset:0,background:'linear-gradient(to top, rgba(0,0,0,0.85), rgba(0,0,0,0.2) 60%, transparent)'}}/>
                  </>
                )}
                <div style={{position:'relative',padding:'10px 12px',fontFamily:'var(--font-display)',fontSize:18,lineHeight:1.1}}>{name || initial?.name || 'Agent'}</div>
              </div>
              <input ref={imgFileRef} type="file" accept="image/png,image/jpeg,image/webp" style={{display:'none'}} onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; uploadImg(f); }}/>
              {/* Editable image prompt — leave blank to let Claude author one
                  from the agent's task; the Regenerate button sits below it. */}
              <textarea
                className="textarea"
                rows={3}
                value={imgPrompt}
                onChange={e => setImgPrompt(e.target.value)}
                placeholder="Image prompt — describe the scene to generate (objects / tools / workspace, no people). Leave blank to auto-write it from the agent's task."
                style={{width:'100%',fontSize:12.5,lineHeight:1.45}}
                disabled={imgBusy}
              />
              <div style={{display:'flex',gap:8,flexWrap:'wrap',marginTop:8}}>
                <button type="button" className="btn-orange" style={{padding:'6px 12px',fontSize:12}} onClick={regenImg} disabled={imgBusy}>{imgBusy ? '…' : (imgUrl ? '↻ Regenerate' : '✦ Generate image')}</button>
                <button type="button" className="btn-ghost" style={{padding:'6px 12px',fontSize:12}} onClick={() => imgFileRef.current?.click()} disabled={imgBusy}>⤒ Upload</button>
                {imgUrl && <button type="button" className="btn-ghost" style={{padding:'6px 12px',fontSize:12,color:'#A20235'}} onClick={deleteImg} disabled={imgBusy}>🗑 Delete</button>}
              </div>
              <div style={{fontSize:11.5,color:'var(--fg-muted)',marginTop:6,lineHeight:1.4}}>Generated on-brand with AI (gpt-image-1), or upload your own (PNG / JPEG / WebP). No image → a plain black card.</div>
              {imgErr && <div style={{fontSize:11.5,color:'#A20235',marginTop:6}}>{imgErr}</div>}
            </div>
          )}
          {/* Type picker — admins only, and only when CREATING. Edits
              don't change type (would alter who can see the agent
              mid-flight). Three options map cleanly to the backend
              (kind, scope) tuple via tupleFromType. Master agents
              aren't listed: they're seeded from migrations, not
              created from the UI. */}
          {isAdmin && isCreate && !isMaster && (
            <div className="field">
              <label>Type</label>
              <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                {[
                  ['system',   'System',   'Admin-managed firm singleton. Manual Run-now trigger. Not visible to regular users.'],
                  ['public',   'Public',   'Admin-managed, visible to every user. Each user runs their own instance.'],
                  ['personal', 'Personal', 'Private to one user. You can create personal agents on behalf of someone — they\'ll see and edit it.'],
                ].map(([val, label, hint]) => (
                  <label key={val} style={{display:'flex',alignItems:'flex-start',gap:8,padding:'8px 10px',border:`1px solid ${pickedType === val ? 'var(--orange)' : 'var(--border)'}`,borderRadius:8,cursor:'pointer',flex:'1 1 200px'}}>
                    <input
                      type="radio"
                      name="agent-type"
                      value={val}
                      checked={pickedType === val}
                      onChange={() => setPickedType(val)}
                      style={{marginTop:3}}
                    />
                    <span>
                      <div style={{fontWeight:600,fontSize:13}}>{label}</div>
                      <div style={{fontSize:11.5,color:'var(--fg-muted)',marginTop:2,lineHeight:1.4}}>{hint}</div>
                    </span>
                  </label>
                ))}
              </div>
              <div style={{fontSize:11,color:'var(--fg-muted)',marginTop:6}}>
                Master agents (firm-wide cron watchers like Eve and the news watchers) are seeded from code — edit existing ones from the System tab; they're not creatable here.
              </div>
            </div>
          )}
          <div className="field">
            <label>{isMaster ? 'Search lens (blank = default)' : 'Prompt template'}</label>
            <textarea
              className="textarea"
              rows={7}
              placeholder={isMaster
                ? 'We\'re a consulting firm. Surface items that would trigger a buying conversation — leadership change, M&A, regulatory pressure, AI / transformation initiatives. Skip product launches and awards.'
                : 'e.g. Prepare the Friday EPH meeting. Pull the last three meeting decks from /logs, draft the new deck with the latest firm snapshot, hand to a Partner for review.'}
              value={promptTemplate}
              onChange={e => setPromptTemplate(e.target.value)}
              maxLength={8000}
            />
            <div style={{fontSize:11,color:'var(--fg-muted)',marginTop:4}}>
              {isMaster
                ? 'Iterating + writing news rows is handled by the runtime; this text only shapes the "what counts" filter for each web_search call. Leave blank to use the firm default for this agent kind.'
                : 'This is what the Intent Router sees on every run. Write it like an intent description — the router will plan a path.'}
            </div>
          </div>

          {/* Schedule — admins only, optional. Two fields: a plain-English
              helper that fills the cron field on an explicit Convert press
              (no live auto-update), and the cron field itself — the stored
              value, directly editable. Blank = manual Run-now only. The
              runtime tick (worker cron, fires every minute) picks up
              scheduled custom + system agents when their cron matches the
              current UTC minute. */}
          {isAdmin && !isMaster && isCreate && (
            <div className="field">
              <label>Schedule (optional)</label>
              <div style={{display:'flex',gap:8,alignItems:'stretch'}}>
                <input
                  className="input"
                  value={scheduleText}
                  onChange={e => setScheduleText(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); convertSchedule(); } }}
                  placeholder="Describe in plain English, e.g. every weekday at 9am"
                  style={{flex:1}}
                />
                <button type="button" className="btn-ghost" onClick={convertSchedule} style={{whiteSpace:'nowrap'}} disabled={!scheduleText.trim()}>
                  Convert ↓
                </button>
              </div>
              {scheduleErr && (
                <div style={{fontSize:11,color:'#A20235',marginTop:4}}>
                  {scheduleErr} Try “every day at 7am”, “weekdays at 9am”, or “every 30 minutes”.
                </div>
              )}
              <input
                className="input"
                value={scheduleCron}
                onChange={e => { setScheduleCron(e.target.value); setScheduleErr(null); }}
                placeholder="0 7 * * *"
                style={{fontFamily:'ui-monospace, SFMono-Regular, monospace',marginTop:8}}
              />
              <div style={{fontSize:11,color:'var(--fg-muted)',marginTop:4,lineHeight:1.5}}>
                {!scheduleCron.trim() ? (
                  <>5-field UTC cron, or type a plain-English description above and press <strong>Convert</strong>. Leave blank for manual Run-now only.<br/>
                  Examples: <code>0 7 * * *</code> = 07:00 daily · <code>0 9 * * 1-5</code> = weekdays 09:00 · <code>*/30 * * * *</code> = every 30 minutes.</>
                ) : cronValid ? (
                  <span style={{color:'var(--fg)'}}>✓ {cronDesc}. <span style={{color:'var(--fg-muted)'}}>Runs in UTC — edit the cron directly, or describe a new one above and press Convert.</span></span>
                ) : (
                  <span style={{color:'#A20235'}}>Not a valid 5-field cron. Fix it, or type a description above and press Convert.</span>
                )}
              </div>
            </div>
          )}

          <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:14,alignItems:'center'}}>
            {isCreate ? (
              <>
                <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
                <button type="submit" className="btn-orange" disabled={busy || (!isMaster && (!name.trim() || !promptTemplate.trim()))}>
                  {busy ? 'Saving…' : 'Create'}
                </button>
              </>
            ) : (
              <>
                <SaveStatus status={saveStatus} error={saveError} dirty={dirty}/>
                <button type="button" className="btn-orange" onClick={() => { saveNow(); onClose(); }}>Done</button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

const RunnerTile = ({ runner }) => {
  const lastTickAgo = runner?.lastTickAt ? Date.now() - new Date(runner.lastTickAt).getTime() : null;
  // Fresh = last tick within 3 minutes (cron is meant to fire every minute).
  const fresh = lastTickAgo !== null && lastTickAgo < 3 * 60_000;
  const stale = lastTickAgo !== null && lastTickAgo >= 3 * 60_000;
  const never = lastTickAgo === null;

  const statusColor = never ? STATUS_COLOR.null : (fresh ? STATUS_COLOR.done : STATUS_COLOR.failed);
  const statusLabel = never ? 'unknown' : (fresh ? 'alive' : 'stale');

  return (
    <div className="kpi" style={{position:'relative',borderLeft:'3px solid #A20235'}}>
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10}}>
        <Icon name="cog" size={12}/>
        <span style={{fontSize:10.5,letterSpacing:'0.08em',textTransform:'uppercase',color:'#A20235',fontWeight:600}}>System · runtime</span>
        <span style={{marginLeft:'auto',fontSize:11,color:'var(--fg-muted)'}}>
          {runner?.lastTickAt ? `last tick ${relTime(runner.lastTickAt)}` : 'never ticked'}
        </span>
      </div>
      <div style={{fontFamily:'var(--font-display)',fontSize:17,lineHeight:1.2,marginBottom:8}}>Worker Queue Runner</div>
      <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:8}}>
        <span style={{fontSize:10.5,padding:'2px 7px',borderRadius:4,background:statusColor.bg,color:statusColor.fg,textTransform:'uppercase',letterSpacing:'0.04em',fontWeight:600}}>
          {statusLabel}
        </span>
        <span style={{fontSize:10.5,padding:'2px 7px',borderRadius:4,background:'var(--bg-card)',color:'var(--fg-muted)'}}>read-only</span>
      </div>
      <div style={{fontSize:11.5,color:'var(--fg-muted)',lineHeight:1.5}}>
        {runner ? (
          <>
            Cron tick every minute · drains the worker queue (skill executions) and one due reflection agent per tick.
            <div style={{marginTop:6,fontFamily:'JetBrains Mono',fontSize:11}}>
              last: {runner.lastWorkers}w / {runner.lastLearning}l<br/>
              total: {runner.totalTicks} ticks · {runner.totalWorkers}w · {runner.totalLearning}l
            </div>
          </>
        ) : (
          <>Cron heartbeat hasn't been recorded yet — either the cron Worker hasn't ticked since the last deploy, or the <code>runner_status</code> table is missing. Run migration 0025.</>
        )}
      </div>
    </div>
  );
};

/* ─── Proposal Builder intake modal ──────────────────────────────
 * Pre-flight form for the Proposal Builder agent. Captures the six
 * intake fields the prompt template asks for, so the planner can
 * skip the intake round-trip and jump straight into Phase 1
 * (Comprehension). File upload at intake isn't wired yet — users
 * paste the RfP text into the first field; the prompt template
 * notes this caveat too.
 */
const IntakeField = ({ label, children, hint }) => (
  <label style={{display:'flex',flexDirection:'column',gap:4}}>
    <span style={{fontSize:10.5,fontWeight:700,letterSpacing:'0.04em',textTransform:'uppercase',color:'var(--fg-muted)'}}>{label}</span>
    {children}
    {hint && <span style={{fontSize:11,color:'var(--fg-muted)',lineHeight:1.4}}>{hint}</span>}
  </label>
);

/**
 * Inline preview of which T-N reminders will fire for the chosen
 * deadline. Mirrors the backend logic in
 * scheduleProposalReminders(): an offset is only scheduled if its
 * computed due time is still in the future. Hidden until both
 * pitch lead and deadline are set (no point promising reminders
 * we won't actually schedule).
 */
const ReminderPreview = ({ deadline, hasPitchLead }) => {
  if (!deadline || !hasPitchLead) return null;
  const deadlineMs = Date.parse(deadline);
  if (!Number.isFinite(deadlineMs)) return null;
  const now = Date.now();
  if (deadlineMs <= now) {
    return (
      <span style={{fontSize:11,color:'#A20235',lineHeight:1.4,marginTop:2}}>
        Deadline is in the past — no reminders will be scheduled.
      </span>
    );
  }
  const ONE_DAY = 86_400_000;
  const offsets = [
    { kind: 'T-14', days: 14, label: '14 days out' },
    { kind: 'T-7',  days:  7, label: '1 week out' },
    { kind: 'T-3',  days:  3, label: '3 days out' },
    { kind: 'T-1',  days:  1, label: 'Day before' },
  ];
  const scheduled = offsets
    .map(o => ({ ...o, dueAt: deadlineMs - o.days * ONE_DAY }))
    .filter(o => o.dueAt > now);

  if (scheduled.length === 0) {
    return (
      <span style={{fontSize:11,color:'var(--fg-muted)',lineHeight:1.4,marginTop:2}}>
        Deadline is less than 24 h away — no reminders to schedule. The Pitch Lead will see the run start notification only.
      </span>
    );
  }
  return (
    <span style={{fontSize:11,color:'var(--fg-muted)',lineHeight:1.45,marginTop:2}}>
      {scheduled.length} reminder{scheduled.length === 1 ? '' : 's'} will be scheduled for the Pitch Lead:{' '}
      {scheduled.map((s, i) => (
        <React.Fragment key={s.kind}>
          {i > 0 && ' · '}
          <span style={{whiteSpace:'nowrap'}}>{s.label} ({new Date(s.dueAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })})</span>
        </React.Fragment>
      ))}
    </span>
  );
};

const ProposalIntakeModal = ({ onLaunch, onCancel, busy }) => {
  const [form, setForm] = React.useState({
    rfpContent: '',
    additionalContext: '',
    clientName: '',
    pitchLeadId: '',
    pitchLeadName: '',
    // 'kind' is the headline question — slide deck or text document.
    // It drives outputFormat below so the rest of the form, the
    // payload, and the planner's "### Output format" heading don't
    // need to change. Users can still override the exact format
    // through the Advanced disclosure.
    kind: 'doc',
    outputFormat: 'PDF',
    submissionDeadline: '',
    pricingModel: 'Fixed price',
    brandVariant: 'Eraneos',
  });
  const [advancedOpen, setAdvancedOpen] = React.useState(false);
  const [people, setPeople] = React.useState(null);
  const [peopleError, setPeopleError] = React.useState(null);

  // RfP files staged in R2 via /api/agents/intake/upload. Each entry
  // is `{key, name, mime, size}`. The run-now POST round-trips these
  // through `inputs.rfpFiles` and the planner calls `read-rfp` on each.
  const [rfpFiles, setRfpFiles] = React.useState([]);
  const [uploading, setUploading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState(null);
  const fileInputRef = React.useRef(null);

  React.useEffect(() => {
    let live = true;
    listPeople()
      .then(p => { if (live) setPeople(p); })
      .catch(err => { if (live) { setPeople([]); setPeopleError(err.message || String(err)); } });
    return () => { live = false; };
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const onPickFiles = async (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length === 0) return;
    setUploadError(null);
    setUploading(true);
    try {
      // Sequential to keep the error message attached to the right file.
      const newFiles = [];
      for (const f of picked) {
        try {
          const uploaded = await uploadProposalIntakeFile(f);
          newFiles.push(uploaded);
        } catch (err) {
          setUploadError(`${f.name}: ${err.message || String(err)}`);
        }
      }
      setRfpFiles(prev => [...prev, ...newFiles]);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeFile = async (key) => {
    // Optimistic: drop from local list immediately so the UI feels
    // instant. If the server-side delete fails the file remains in
    // R2 as an orphan — which is the same state as before this
    // commit, so worst case is no regression. Logging the error
    // gives admins a way to find lingering bytes.
    setRfpFiles(prev => prev.filter(f => f.key !== key));
    try {
      await deleteProposalIntakeFile(key);
    } catch (err) {
      console.warn('intake-file delete failed; orphan left in R2:', key, err);
    }
  };

  // RfP source is satisfied by EITHER pasted text OR an uploaded
  // file (Phase 2b — file upload now wired; paste is the fallback).
  const hasRfpSource = form.rfpContent.trim().length > 0 || rfpFiles.length > 0;
  const canSubmit = hasRfpSource && form.pitchLeadName && form.submissionDeadline && !busy && !uploading;

  const submit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    onLaunch({
      rfpContent: form.rfpContent.trim(),
      additionalContext: form.additionalContext.trim(),
      clientName: form.clientName.trim(),
      pitchLeadId: form.pitchLeadId,
      pitchLeadName: form.pitchLeadName,
      outputFormat: form.outputFormat,
      submissionDeadline: form.submissionDeadline,
      pricingModel: form.pricingModel,
      brandVariant: form.brandVariant,
      rfpFiles,
    });
  };

  return (
    // Cancel / close stay enabled while `busy`. run-now blocks for many
    // seconds (planner LLM call + startRun) and disabling every dismiss
    // affordance during that window traps the user. Closing just hides
    // the form — the launch finishes in the background and reports back
    // via the page banner; Stop on the run board aborts an actual run.
    <div className="modal-mask" onClick={onCancel}>
      <div
        className="modal"
        onClick={e => e.stopPropagation()}
        style={{position:'relative',maxWidth:900,width:'96%'}}
      >
        <button className="modal__close" onClick={onCancel}>×</button>
        <div style={{fontFamily:'var(--font-display)',fontSize:22,marginBottom:4}}>Proposal Builder — intake</div>
        <div style={{fontSize:12.5,color:'var(--fg-muted)',marginBottom:18,lineHeight:1.55,maxWidth:'70ch'}}>
          These answers replace the agent's opening intake questions. Upload the RfP as PDF, DOCX, PPTX, XLSX, TXT, or Markdown and the planner reads it directly — or paste the text below. The selections + file references are stored on the run for audit.
        </div>
        <form onSubmit={submit} className="intake-form">
          <div className="intake-cols">
            <div className="intake-col">
              <IntakeField
                label="RfP files (PDF / DOCX / PPTX / XLSX) — upload one or more"
                hint="Stored in R2; the planner reads each one. No file? Paste the text below instead."
              >
                <div style={{display:'flex',flexDirection:'column',gap:8}}>
                  <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                    <button
                      type="button"
                      className="btn-ghost"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading || busy}
                      style={{padding:'6px 14px',fontSize:13,border:'1px dashed var(--border)',borderRadius:6}}
                    >
                      {uploading ? 'Uploading…' : '+ Add RfP file(s)'}
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept=".pdf,.docx,.pptx,.xlsx,.txt,.md,application/pdf,text/plain,text/markdown"
                      onChange={onPickFiles}
                      style={{display:'none'}}
                    />
                    {uploadError && <span style={{fontSize:12,color:'#A20235'}}>{uploadError}</span>}
                  </div>
                  {rfpFiles.length > 0 && (
                    <div style={{display:'flex',flexDirection:'column',gap:4}}>
                      {rfpFiles.map(f => (
                        <div key={f.key} style={{display:'flex',alignItems:'center',gap:8,padding:'6px 10px',background:'var(--bg-card)',borderRadius:6,fontSize:12.5}}>
                          <span style={{flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={f.name}>{f.name}</span>
                          <span style={{color:'var(--fg-muted)',fontSize:11}}>{(f.size / 1024).toFixed(1)} KB</span>
                          <button
                            type="button"
                            onClick={() => removeFile(f.key)}
                            disabled={busy}
                            style={{all:'unset',cursor:'pointer',color:'var(--fg-muted)',fontSize:14,lineHeight:1}}
                            title="Remove from this run"
                          >×</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </IntakeField>

              <IntakeField
                label={`RfP content (paste) ${rfpFiles.length > 0 ? '' : '*'}`}
                hint={rfpFiles.length > 0
                  ? "Optional — files above will be extracted by the planner. Use this field if there's extra context that didn't make it into the PDF."
                  : "Copy from the PDF / DOCX / email and paste here. Required when no file is uploaded."}
              >
                <textarea
                  value={form.rfpContent}
                  onChange={e => set('rfpContent', e.target.value)}
                  rows={rfpFiles.length > 0 ? 3 : 5}
                  required={rfpFiles.length === 0}
                  style={{fontFamily:'inherit',fontSize:13,padding:10,width:'100%',resize:'vertical',border:'1px solid var(--border)',borderRadius:6,background:'var(--bg-card)',color:'inherit'}}
                />
              </IntakeField>

              <IntakeField label="Additional context (optional)" hint="Verbal briefing notes, client signals, prior conversations.">
                <textarea
                  value={form.additionalContext}
                  onChange={e => set('additionalContext', e.target.value)}
                  rows={2}
                  style={{fontFamily:'inherit',fontSize:13,padding:10,width:'100%',resize:'vertical',border:'1px solid var(--border)',borderRadius:6,background:'var(--bg-card)',color:'inherit'}}
                />
              </IntakeField>

              <IntakeField label="Submission deadline *">
                <input
                  type="datetime-local"
                  value={form.submissionDeadline}
                  onChange={e => set('submissionDeadline', e.target.value)}
                  required
                  style={{fontSize:13,padding:8,width:'100%',border:'1px solid var(--border)',borderRadius:6,background:'var(--bg-card)',color:'inherit'}}
                />
                <ReminderPreview deadline={form.submissionDeadline} hasPitchLead={!!form.pitchLeadId}/>
              </IntakeField>
            </div>

            <div className="intake-col">
              <IntakeField label="Deliverable *" hint="Pick the shape; the exact file format defaults sensibly and you can change it under Advanced. All formats ship with an HTML preview and on-demand PDF/PPTX export from the run page.">
                <div className="respo-2col" style={{gap:8}}>
                  {[
                    { id: 'doc',  label: 'Text document', sub: 'Memo or proposal — PDF/DOCX primary, section-by-section structure',  defaultFormat: 'PDF'  },
                    { id: 'deck', label: 'Slide deck',    sub: 'PPTX primary — per-slide commenting + regenerate in the deck viewer', defaultFormat: 'PPTX' },
                  ].map(opt => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => { set('kind', opt.id); set('outputFormat', opt.defaultFormat); }}
                      style={{
                        display:'flex',flexDirection:'column',alignItems:'flex-start',gap:4,
                        padding:'10px 12px',borderRadius:8,cursor:'pointer',textAlign:'left',
                        background: form.kind === opt.id ? 'var(--orange-tint, rgba(240,130,0,0.08))' : 'var(--bg-card)',
                        border: form.kind === opt.id ? '2px solid var(--orange)' : '1px solid var(--border)',
                        color:'inherit',
                      }}
                    >
                      <strong style={{fontSize:14}}>{opt.label}</strong>
                      <span style={{fontSize:11.5,color:'var(--fg-muted)',lineHeight:1.4}}>{opt.sub}</span>
                    </button>
                  ))}
                </div>
              </IntakeField>

              <div className="respo-2col" style={{gap:'var(--space-4)'}}>
                <IntakeField label="Pitch Lead *" hint="Owns approvals, deadline reminders, and sign-off.">
                  {people === null ? (
                    <span style={{fontSize:12,color:'var(--fg-muted)'}}>Loading firm directory…</span>
                  ) : peopleError ? (
                    <span style={{fontSize:12,color:'#A20235'}}>Couldn't load directory: {peopleError}</span>
                  ) : (
                    <select
                      value={form.pitchLeadId}
                      onChange={e => {
                        const pid = e.target.value;
                        const p = people.find(x => x.id === pid);
                        setForm(f => ({ ...f, pitchLeadId: pid, pitchLeadName: p ? p.name : '' }));
                      }}
                      required
                      style={{fontSize:13,padding:8,width:'100%',border:'1px solid var(--border)',borderRadius:6,background:'var(--bg-card)',color:'inherit'}}
                    >
                      <option value="" disabled>Select a Pitch Lead…</option>
                      {people.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.name}{p.title ? ` — ${p.title}` : ''}
                        </option>
                      ))}
                    </select>
                  )}
                </IntakeField>

                <IntakeField label="Pricing model *">
                  <select
                    value={form.pricingModel}
                    onChange={e => set('pricingModel', e.target.value)}
                    style={{fontSize:13,padding:8,width:'100%',border:'1px solid var(--border)',borderRadius:6,background:'var(--bg-card)',color:'inherit'}}
                  >
                    <option>Fixed price</option>
                    <option>Day rates</option>
                    <option>Weekly rates</option>
                    <option>Performance-based</option>
                    <option>Mix</option>
                  </select>
                </IntakeField>
              </div>

              <div className="respo-2col" style={{gap:'var(--space-4)'}}>
                <IntakeField label="Client / addressee (optional)" hint="Shown on the cover as 'Proposal for <client>'. Blank = default subtitle.">
                  <input
                    type="text"
                    value={form.clientName}
                    onChange={e => set('clientName', e.target.value)}
                    placeholder="e.g. AcmeCorp Procurement"
                    style={{fontSize:13,padding:8,width:'100%',border:'1px solid var(--border)',borderRadius:6,background:'var(--bg-card)',color:'inherit'}}
                  />
                </IntakeField>

                <IntakeField label="Brand variant *" hint="Eve is the strategy sub-brand — peach gradient cover + 'eve by eraneos' wordmark.">
                  <select
                    value={form.brandVariant}
                    onChange={e => set('brandVariant', e.target.value)}
                    style={{fontSize:13,padding:8,width:'100%',border:'1px solid var(--border)',borderRadius:6,background:'var(--bg-card)',color:'inherit'}}
                  >
                    <option>Eraneos</option>
                    <option>Eve by Eraneos</option>
                  </select>
                </IntakeField>
              </div>

              <details
                open={advancedOpen}
                onToggle={e => setAdvancedOpen(e.target.open)}
                style={{fontSize:12,color:'var(--fg-muted)'}}
              >
                <summary style={{cursor:'pointer',userSelect:'none'}}>
                  Advanced: override the file format (default for {form.kind === 'deck' ? 'Slide deck' : 'Text document'}: {form.outputFormat})
                </summary>
                <div style={{marginTop:8}}>
                  <select
                    value={form.outputFormat}
                    onChange={e => set('outputFormat', e.target.value)}
                    style={{fontSize:13,padding:8,width:'100%',border:'1px solid var(--border)',borderRadius:6,background:'var(--bg-card)',color:'inherit'}}
                  >
                    <option>PDF</option>
                    <option>PPTX</option>
                    <option>DOCX</option>
                    <option>XLSX</option>
                    <option>Combination</option>
                  </select>
                  <div style={{marginTop:6,fontSize:11.5,lineHeight:1.5}}>
                    Pick a specific binary as the primary download. Cross-format export (PDF from a deck, HTML preview from either) is always available from the run page once it finishes — so this only changes which file you get as the up-front download.
                  </div>
                </div>
              </details>
            </div>
          </div>

          <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
            <button type="button" className="btn-ghost" onClick={onCancel} style={{padding:'6px 14px',fontSize:13}}>Cancel</button>
            <button type="submit" className="btn-orange" disabled={!canSubmit} style={{padding:'6px 14px',fontSize:13}}>
              {busy ? 'Launching…' : 'Launch run'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ─── Redesign in Eraneos intake modal ───────────────────────────
 * Simpler than the Proposal Builder intake. Just three inputs:
 * source (file upload + paste fallback), output format, brand
 * variant. "Transform" launches the run.
 *
 * Reuses the same intake-file upload + delete API the Proposal
 * Builder uses, so files land at u/<userId>/proposal-intake/<uuid>.<ext>
 * regardless of which agent reads them — the read-rfp skill is
 * file-agnostic.
 */
const RedesignIntakeModal = ({ onLaunch, onCancel, busy }) => {
  const [form, setForm] = React.useState({
    rfpContent: '',
    // 'kind' is the headline question; outputFormat is derived from
    // it and (optionally) overridden in the Advanced disclosure.
    kind: 'doc',
    outputFormat: 'PDF',
    brandVariant: 'Eraneos',
  });
  const [advancedOpen, setAdvancedOpen] = React.useState(false);
  const [rfpFiles, setRfpFiles] = React.useState([]);
  const [uploading, setUploading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState(null);
  const fileInputRef = React.useRef(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const onPickFiles = async (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length === 0) return;
    setUploadError(null);
    setUploading(true);
    try {
      const newFiles = [];
      for (const f of picked) {
        try { newFiles.push(await uploadProposalIntakeFile(f)); }
        catch (err) { setUploadError(`${f.name}: ${err.message || String(err)}`); }
      }
      setRfpFiles(prev => [...prev, ...newFiles]);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeFile = async (key) => {
    setRfpFiles(prev => prev.filter(f => f.key !== key));
    try { await deleteProposalIntakeFile(key); } catch (err) { console.warn('intake delete failed:', key, err); }
  };

  const hasSource = form.rfpContent.trim().length > 0 || rfpFiles.length > 0;
  const canSubmit = hasSource && !busy && !uploading;

  const submit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    onLaunch({
      rfpContent:   form.rfpContent.trim(),
      outputFormat: form.outputFormat,
      brandVariant: form.brandVariant,
      rfpFiles,
    });
  };

  return (
    <div className="modal-mask" onClick={onCancel}>
      <div
        className="modal"
        onClick={e => e.stopPropagation()}
        style={{position:'relative',maxWidth:800,width:'95%'}}
      >
        <button className="modal__close" onClick={onCancel}>×</button>
        <div style={{fontFamily:'var(--font-display)',fontSize:22,marginBottom:4}}>Redesign in Eraneos</div>
        <div style={{fontSize:12.5,color:'var(--fg-muted)',marginBottom:18,lineHeight:1.55,maxWidth:'64ch'}}>
          Drop in any source artefact — PDF, DOCX, PPTX, XLSX, or text. Pick the format you want back. The agent reads the source, restyles it in Eraneos voice, and renders a real binary file with the full brand applied (cover page, header / footer, brand colours, Mona Sans + Eraneos Display, variant overlay).
        </div>
        <form onSubmit={submit} className="intake-form">
          <div className="intake-cols">
            <div className="intake-col">
              <IntakeField
                label="Source file (PDF / DOCX / PPTX / XLSX) — upload one or more"
                hint="Each file is stored in R2 and the agent calls the `read-rfp` skill to extract its content. Skip and paste below if you don't have the file handy."
              >
                <div style={{display:'flex',flexDirection:'column',gap:8}}>
                  <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                    <button
                      type="button"
                      className="btn-ghost"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading || busy}
                      style={{padding:'6px 14px',fontSize:13,border:'1px dashed var(--border)',borderRadius:6}}
                    >
                      {uploading ? 'Uploading…' : '+ Add source file(s)'}
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept=".pdf,.docx,.pptx,.xlsx,.txt,.md,application/pdf,text/plain,text/markdown"
                      onChange={onPickFiles}
                      style={{display:'none'}}
                    />
                    {uploadError && <span style={{fontSize:12,color:'#A20235'}}>{uploadError}</span>}
                  </div>
                  {rfpFiles.length > 0 && (
                    <div style={{display:'flex',flexDirection:'column',gap:4}}>
                      {rfpFiles.map(f => (
                        <div key={f.key} style={{display:'flex',alignItems:'center',gap:8,padding:'6px 10px',background:'var(--bg-card)',borderRadius:6,fontSize:12.5}}>
                          <span style={{flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={f.name}>{f.name}</span>
                          <span style={{color:'var(--fg-muted)',fontSize:11}}>{(f.size / 1024).toFixed(1)} KB</span>
                          <button
                            type="button"
                            onClick={() => removeFile(f.key)}
                            disabled={busy}
                            style={{all:'unset',cursor:'pointer',color:'var(--fg-muted)',fontSize:14,lineHeight:1}}
                            title="Remove from this run"
                          >×</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </IntakeField>

              <IntakeField
                label={`Source text (paste) ${rfpFiles.length > 0 ? '' : '*'}`}
                hint={rfpFiles.length > 0
                  ? "Optional — files above will be extracted by the agent. Use this field for extra context that didn't make it into the source file."
                  : "Paste the source content here when no file is uploaded."}
              >
                <textarea
                  value={form.rfpContent}
                  onChange={e => set('rfpContent', e.target.value)}
                  rows={rfpFiles.length > 0 ? 3 : 6}
                  required={rfpFiles.length === 0}
                  style={{fontFamily:'inherit',fontSize:13,padding:10,width:'100%',resize:'vertical',border:'1px solid var(--border)',borderRadius:6,background:'var(--bg-card)',color:'inherit'}}
                />
              </IntakeField>
            </div>

            <div className="intake-col">
              <IntakeField label="Deliverable *" hint="Pick the shape; the exact file format below defaults sensibly and you can change it under Advanced. All formats now ship with an HTML preview and on-demand PDF/PPTX export from the run page.">
                <div style={{display:'flex',flexDirection:'column',gap:8}}>
                  {[
                    { id: 'doc',  label: 'Text document', sub: 'Re-stylise as a PDF/DOCX memo — clean section-by-section structure',  defaultFormat: 'PDF'  },
                    { id: 'deck', label: 'Slide deck',    sub: 'Re-stylise as a PPTX deck — per-slide commenting + regenerate flow', defaultFormat: 'PPTX' },
                  ].map(opt => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => { set('kind', opt.id); set('outputFormat', opt.defaultFormat); }}
                      style={{
                        display:'flex',flexDirection:'column',alignItems:'flex-start',gap:2,
                        padding:'10px 12px',borderRadius:8,cursor:'pointer',textAlign:'left',
                        background: form.kind === opt.id ? 'var(--orange-tint, rgba(240,130,0,0.08))' : 'var(--bg-card)',
                        border: form.kind === opt.id ? '2px solid var(--orange)' : '1px solid var(--border)',
                        color:'inherit',
                      }}
                    >
                      <strong style={{fontSize:14}}>{opt.label}</strong>
                      <span style={{fontSize:11.5,color:'var(--fg-muted)',lineHeight:1.4}}>{opt.sub}</span>
                    </button>
                  ))}
                </div>
              </IntakeField>

              <IntakeField label="Brand variant *" hint="Eraneos default; pick Eve for the sub-brand.">
                <select
                  value={form.brandVariant}
                  onChange={e => set('brandVariant', e.target.value)}
                  style={{fontSize:13,padding:8,width:'100%',border:'1px solid var(--border)',borderRadius:6,background:'var(--bg-card)',color:'inherit'}}
                >
                  <option>Eraneos</option>
                  <option>Eve by Eraneos</option>
                </select>
              </IntakeField>

              <details
                open={advancedOpen}
                onToggle={e => setAdvancedOpen(e.target.open)}
                style={{fontSize:12,color:'var(--fg-muted)'}}
              >
                <summary style={{cursor:'pointer',userSelect:'none'}}>
                  Advanced: override the file format (default for {form.kind === 'deck' ? 'Slide deck' : 'Text document'}: {form.outputFormat})
                </summary>
                <div style={{marginTop:8}}>
                  <select
                    value={form.outputFormat}
                    onChange={e => set('outputFormat', e.target.value)}
                    style={{fontSize:13,padding:8,width:'100%',border:'1px solid var(--border)',borderRadius:6,background:'var(--bg-card)',color:'inherit'}}
                  >
                    <option>PDF</option>
                    <option>DOCX</option>
                    <option>PPTX</option>
                    <option value="XLSX">XLSX (experimental)</option>
                  </select>
                  {form.outputFormat === 'XLSX' && (
                    <div style={{marginTop:6,fontSize:11.5,color:'#A20235',lineHeight:1.4}}>
                      XLSX is experimental — only useful when the source has real tabular data. Prose-heavy sources convert to thin sheets.
                    </div>
                  )}
                  <div style={{marginTop:6,fontSize:11.5,lineHeight:1.5}}>
                    Cross-format export (PDF from a deck, HTML preview from either) is always available from the run page once it finishes — so this only changes which file you get as the up-front download.
                  </div>
                </div>
              </details>
            </div>
          </div>

          <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
            <button type="button" className="btn-ghost" onClick={onCancel} style={{padding:'6px 14px',fontSize:13}}>Cancel</button>
            <button type="submit" className="btn-orange" disabled={!canSubmit} style={{padding:'6px 14px',fontSize:13,fontWeight:600}}>
              {busy ? 'Transforming…' : 'Transform'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ─── Markdown Converter intake modal ────────────────────────────
 * The simplest intake: just a file picker. Upload any supported file
 * (PDF / DOCX / PPTX / XLSX / text / CSV / JSON / HTML / code / image)
 * and "Convert" launches the run. Files land at
 * u/<userId>/proposal-intake/<uuid>.<ext> via the shared intake-upload
 * API; the agent reads them with the `file-to-markdown` skill, which
 * accepts any file the caller owns regardless of prefix.
 */
const MarkdownIntakeModal = ({ onLaunch, onCancel, busy }) => {
  const [files, setFiles] = React.useState([]);
  const [uploading, setUploading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState(null);
  const fileInputRef = React.useRef(null);

  const onPickFiles = async (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length === 0) return;
    setUploadError(null);
    setUploading(true);
    try {
      const added = [];
      for (const f of picked) {
        try { added.push(await uploadProposalIntakeFile(f)); }
        catch (err) { setUploadError(`${f.name}: ${err.message || String(err)}`); }
      }
      setFiles(prev => [...prev, ...added]);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeFile = async (key) => {
    setFiles(prev => prev.filter(f => f.key !== key));
    try { await deleteProposalIntakeFile(key); } catch (err) { console.warn('intake delete failed:', key, err); }
  };

  const canSubmit = files.length > 0 && !busy && !uploading;
  const submit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    onLaunch({ sourceFiles: files });
  };

  return (
    <div className="modal-mask" onClick={onCancel}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{position:'relative',maxWidth:640,width:'95%'}}>
        <button className="modal__close" onClick={onCancel}>×</button>
        <div style={{fontFamily:'var(--font-display)',fontSize:22,marginBottom:4}}>Markdown Converter</div>
        <div style={{fontSize:12.5,color:'var(--fg-muted)',marginBottom:18,lineHeight:1.55,maxWidth:'64ch'}}>
          Upload any file — PDF, Word / PowerPoint / Excel, text, CSV, JSON, HTML, source code, or an image — and the agent converts it to clean Markdown. Add several to convert them all in one run.
        </div>
        <form onSubmit={submit} className="intake-form">
          <IntakeField
            label="Files to convert *"
            hint="Each file is stored and the agent calls the `file-to-markdown` skill on it. PDF · DOCX / PPTX / XLSX · TXT / MD / CSV / TSV / JSON / YAML / XML / HTML / source code · PNG / JPG / GIF / WEBP."
          >
            <div style={{display:'flex',flexDirection:'column',gap:8}}>
              <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading || busy}
                  style={{padding:'6px 14px',fontSize:13,border:'1px dashed var(--border)',borderRadius:6}}
                >
                  {uploading ? 'Uploading…' : '+ Add file(s)'}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.docx,.pptx,.xlsx,.txt,.md,.markdown,.rst,.log,.csv,.tsv,.json,.yaml,.yml,.xml,.svg,.html,.htm,.js,.jsx,.ts,.tsx,.py,.rb,.go,.rs,.java,.c,.h,.cpp,.cs,.php,.sh,.sql,.css,.scss,.png,.jpg,.jpeg,.gif,.webp"
                  onChange={onPickFiles}
                  style={{display:'none'}}
                />
                {uploadError && <span style={{fontSize:12,color:'#A20235'}}>{uploadError}</span>}
              </div>
              {files.length > 0 && (
                <div style={{display:'flex',flexDirection:'column',gap:4}}>
                  {files.map(f => (
                    <div key={f.key} style={{display:'flex',alignItems:'center',gap:8,padding:'6px 10px',background:'var(--bg-card)',borderRadius:6,fontSize:12.5}}>
                      <span style={{flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={f.name}>{f.name}</span>
                      <span style={{color:'var(--fg-muted)',fontSize:11}}>{(f.size / 1024).toFixed(1)} KB</span>
                      <button
                        type="button"
                        onClick={() => removeFile(f.key)}
                        disabled={busy}
                        style={{all:'unset',cursor:'pointer',color:'var(--fg-muted)',fontSize:14,lineHeight:1}}
                        title="Remove from this run"
                      >×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </IntakeField>

          <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:18}}>
            <button type="button" className="btn-ghost" onClick={onCancel} style={{padding:'6px 14px',fontSize:13}}>Cancel</button>
            <button type="submit" className="btn-orange" disabled={!canSubmit} style={{padding:'6px 14px',fontSize:13,fontWeight:600}}>
              {busy ? 'Converting…' : 'Convert to Markdown'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* Cover-imagery generation styles. Mirrors { id, label, group } of the
 * canonical list in functions/_lib/image-styles.ts — keep in sync. The id is
 * the contract (the backend falls back to its default style on an unknown id),
 * so a label drift is harmless but an id must match. Only shown when the
 * imagery source is "AI-generated". */
const IMAGE_STYLES = [
  { id: 'photoreal',           label: 'Photo-Realistic',            group: 'Photographic'  },
  { id: 'documentary-photo',   label: 'Documentary Photograph',     group: 'Photographic'  },
  { id: 'editorial-magazine',  label: 'Editorial Magazine (Forbes)', group: 'Photographic' },
  { id: 'cinematic-still',     label: 'Cinematic Film Still',       group: 'Photographic'  },
  { id: 'architectural',       label: 'Architectural Photography',  group: 'Photographic'  },
  { id: 'aerial-drone',        label: 'Aerial / Drone',             group: 'Photographic'  },
  { id: 'monochrome-fineart',  label: 'Black & White Fine Art',     group: 'Photographic'  },
  { id: 'oil-painting',        label: 'Classical Oil Painting',     group: 'Painterly'     },
  { id: 'impressionist',       label: 'Impressionist Painting',     group: 'Painterly'     },
  { id: 'watercolor',          label: 'Watercolour Wash',           group: 'Painterly'     },
  { id: 'ink-wash',            label: 'Ink & Wash (Sumi-e)',        group: 'Painterly'     },
  { id: 'abstract-geometric',  label: 'Abstract Geometric',         group: 'Abstract'      },
  { id: 'abstract-fluid',      label: 'Abstract Fluid',             group: 'Abstract'      },
  { id: 'gradient-aurora',     label: 'Gradient Aurora',            group: 'Abstract'      },
  { id: 'macro-texture',       label: 'Macro Texture',              group: 'Abstract'      },
  { id: 'flat-vector',         label: 'Flat Vector Illustration',   group: 'Illustration'  },
  { id: 'isometric',           label: 'Isometric Illustration',     group: 'Illustration'  },
  { id: 'line-art',            label: 'Line Art / Blueprint',       group: 'Illustration'  },
  { id: 'render-3d',           label: '3D Render',                  group: '3D & Digital'  },
  { id: 'low-poly',            label: 'Low-Poly / Crystalline',     group: '3D & Digital'  },
  { id: 'double-exposure',     label: 'Double Exposure',            group: '3D & Digital'  },
];
const IMAGE_STYLE_GROUPS = ['Photographic', 'Painterly', 'Abstract', 'Illustration', '3D & Digital'];

/* ── Slide-Deck Creator intake modal ────────────────────────────────
 *
 * Mirrors the Claude-Design / NotebookLM question flow: working
 * title → audience → goal → key message → optional source files /
 * text → output length + brand variant. Submits to the same
 * runAgentNow() endpoint as the other intake modals; the backend's
 * formatIntakeBlock turns these fields into the "### Working title /
 * ### Audience / ..." headings the agent prompt reads.
 */
// One-line fidelity directives, keyed by the intake radio choice. These
// land verbatim on the "### Fidelity" intake line and are read by
// extractDeckFidelity → deck-briefing / deck-storyliner / deck-drafter.
// Keep each a SINGLE line (extractDeckFidelity captures one line).
const FIDELITY_DIRECTIVE = {
  optimize:  'Optimise freely — the slide count, the source’s structure and its wording are STARTING POINTS, not a contract. Recommend the ideal slide count, re-sequence the narrative, and rewrite copy wherever it makes the deck land harder.',
  structure: 'Strict on the slide count and the source’s story order — keep them as given; optimise / polish the WORDING only.',
  exact:     'Strict on everything — keep the slide count, the story order AND the source’s wording / titles verbatim where the user gave them; do not paraphrase, reorder, add or drop slides.',
};

const SlideDeckIntakeModal = ({ onLaunch, onCancel, busy }) => {
  const [form, setForm] = React.useState({
    sourceMaterial: '',
    // Optional structured context (collapsed by default). Folded into the source
    // on submit so the briefing/storyliner get richer, grounded input — the
    // biggest quality lever for analytical / consulting decks. Blank → ignored.
    audience: '',
    knownFacts: '',
    constraints: '',
    // Categorical length — drives the deck-briefing skill's
    // recommended slide count. 'short' = 1-5, 'medium' = 6-15,
    // 'extensive' = 16-50. The briefing step refines this into an
    // exact count the user approves before any slides are drafted.
    length: 'medium',
    // How strictly the briefing / storyliner honour the slide count and
    // the source's own structure & wording. 'optimize' (default) treats
    // them as starting points; 'structure' locks the count + story order
    // but polishes wording; 'exact' keeps count, order AND wording. Maps
    // to a "### Fidelity" intake line the backend reads via
    // extractDeckFidelity.
    fidelity: 'optimize',
    brandVariant: 'Eraneos',
    // How a cover with no match in the image library is filled:
    // 'unsplash' (on-brand stock search, the default) or 'openai'
    // (gpt-image-1 generation in the chosen style). imageStyle only
    // matters in 'openai' mode.
    imageSource: 'editorial',
    imageStyle: 'photoreal',
    // How section / chapter dividers look in the PHOTO modes (Unsplash / AI),
    // for BOTH slide creators. 'photo' (default) = full-bleed topical photo,
    // text-only · 'free' = a typographic / designed divider, no fetched photo ·
    // 'illustration' = a hand-drawn line-art motif, no photo (lets a photo deck
    // still have drawn dividers). Unused in the deck-wide Illustration imagery
    // mode (whole deck is already drawn) and in Photo-library mode.
    dividerStyle: 'photo',
    // Which engine builds the deck. 'classic' = the agent pipeline (the 15
    // brand archetypes, server-rendered, full review/export workflow).
    // 'mdfirst' = Slide creator 2: the md-first template/element system,
    // composed in one pass and opened in the same viewer for on-canvas editing.
    engine: 'mdfirst',
  });
  const [sourceFiles, setSourceFiles] = React.useState([]);
  const [uploading, setUploading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState(null);
  const [showContext, setShowContext] = React.useState(false);
  const fileInputRef = React.useRef(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const onPickFiles = async (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length === 0) return;
    setUploadError(null);
    setUploading(true);
    try {
      const newFiles = [];
      for (const f of picked) {
        try { newFiles.push(await uploadProposalIntakeFile(f)); }
        catch (err) { setUploadError(`${f.name}: ${err.message || String(err)}`); }
      }
      setSourceFiles(prev => [...prev, ...newFiles]);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeFile = async (key) => {
    setSourceFiles(prev => prev.filter(f => f.key !== key));
    try { await deleteProposalIntakeFile(key); } catch (err) { console.warn('intake delete failed:', key, err); }
  };

  // At least one source — files OR text. Audience / goal / key message
  // used to be required fields here; we now infer them from the source
  // (or have the agent ask via a human_task step in the flow when it
  // genuinely can't tell).
  const hasSource = form.sourceMaterial.trim().length > 0 || sourceFiles.length > 0;
  const canSubmit = hasSource && !busy && !uploading;

  const submit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    // Fold the optional structured context into the source so it reaches the
    // briefing/storyliner regardless of how the planner maps intake keys. Clearly
    // headed so the model treats each as what it is. Blank fields are dropped.
    // (audience is passed as a first-class intake key below, so it's not folded
    // here — only the fields without a dedicated key are.)
    const contextBlocks = [
      form.knownFacts.trim() ? `### Known facts & sources (ground on these; verify/label the rest)\n${form.knownFacts.trim()}` : '',
      form.constraints.trim()? `### Constraints & scope\n${form.constraints.trim()}` : '',
    ].filter(Boolean).join('\n\n');
    const composedSource = [form.sourceMaterial.trim(), contextBlocks].filter(Boolean).join('\n\n');
    onLaunch({
      sourceMaterial: composedSource,
      audience: form.audience.trim(),
      // Send the categorical length AND a target count derived from
      // it. The briefing step uses both — the category for tone /
      // depth, the count as a starting point the user can revise on
      // the briefing approval screen.
      length:       form.length,
      slideCount:   form.length === 'short' ? '5' : form.length === 'extensive' ? '30' : '10',
      // Slide-Deck Creator no longer renders a PPTX up-front. The
      // chain stops after deck-drafter; rendering runs on demand
      // from the deck viewer once every slide is approved. We still
      // pass outputFormat='PPTX' so anything that reads the
      // formatIntakeBlock heading (legacy router heuristics) sees
      // a consistent value.
      outputFormat: 'PPTX',
      // Deck type is no longer asked up front — always sent blank so the
      // briefing auto-detects the storyline arc & tone from the source.
      deckType: '',
      // Fidelity directive — how strictly to honour the slide count and
      // the source's structure/wording. Sent as a one-line directive the
      // backend reads via extractDeckFidelity into the briefing/storyliner/
      // drafter prompts.
      fidelity: FIDELITY_DIRECTIVE[form.fidelity] || FIDELITY_DIRECTIVE.optimize,
      brandVariant: form.brandVariant,
      // Cover imagery: how to fill a cover not already in the library, and
      // (for openai) the generation style. The backend reads these off the
      // intake block; absent → 'unsplash' (the historical default).
      imageMode: form.imageSource,
      imageStyle: form.imageStyle,
      // ONE style through the deck: section dividers follow the chosen visual
      // style rather than being a separate knob. Drawn styles (illustration /
      // editorial) → drawn dividers; every photo/AI style → full-bleed image
      // dividers in that same style. (Read by both creators.)
      dividerStyle: (form.imageSource === 'illustration' || form.imageSource === 'editorial') ? 'illustration' : 'photo',
      // Engine selector — 'classic' (agent pipeline) or 'mdfirst' (Slide creator 2).
      engine: form.engine,
      sourceFiles,
    });
  };

  return (
    <div className="modal-mask" onClick={onCancel}>
      <div
        className="modal"
        onClick={e => e.stopPropagation()}
        style={{position:'relative',maxWidth:840,width:'95%'}}
      >
        <button className="modal__close" onClick={onCancel}>×</button>
        <div style={{fontFamily:'var(--font-display)',fontSize:'var(--text-2xl)',marginBottom:4}}>Slide-Deck Creator</div>
        <div style={{fontSize:'var(--text-sm)',color:'var(--fg-muted)',marginBottom:'var(--space-5)',lineHeight:'var(--lh-normal)',maxWidth:'62ch'}}>
          Drop in your source — files, pasted text, or both — and the agent designs the story arc, drafts the slides, and renders a real .pptx. If the agent can't infer audience, goal, or the key takeaway from the material, it will pause and ask you mid-run with a short multiple-choice prompt rather than make you answer everything up front.
        </div>
        <form onSubmit={submit} className="intake-form">
          {/* Engine selector — same intake + viewer for both; only the slide
              generator differs. Creator 1 = brand archetypes via the agent
              pipeline; Creator 2 = the md-first template/element system. */}
          <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-4)', flexWrap: 'wrap' }}>
            {[
              { id: 'classic', label: 'Slide creator 1', sub: 'Brand archetypes · full review + export' },
              { id: 'mdfirst', label: 'Slide creator 2', sub: 'md-first system · on-canvas editing' },
            ].map(opt => (
              <button
                key={opt.id}
                type="button"
                onClick={() => set('engine', opt.id)}
                disabled={busy}
                style={{
                  flex: '1 1 220px', textAlign: 'left', cursor: 'pointer', padding: '10px 14px', borderRadius: 'var(--r-md)',
                  border: `1.5px solid ${form.engine === opt.id ? 'var(--orange, #FF6428)' : 'var(--border)'}`,
                  background: form.engine === opt.id ? 'rgba(255,100,40,0.07)' : 'var(--bg)',
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 14 }}>{form.engine === opt.id ? '✓ ' : ''}{opt.label}</div>
                <div style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 2 }}>{opt.sub}</div>
              </button>
            ))}
          </div>
          <div className="intake-cols">
            <div className="intake-col">
              <IntakeField
                label="Source material *"
                hint="Drop in PDF / DOCX / PPTX / XLSX / TXT / Markdown, or paste text below. At least one is required — everything else the agent infers or asks for during the run."
              >
                <div style={{display:'flex',flexDirection:'column',gap:'var(--space-2)'}}>
                  <div style={{display:'flex',alignItems:'center',gap:'var(--space-2)',flexWrap:'wrap'}}>
                    <button
                      type="button"
                      className="btn-ghost"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading || busy}
                      style={{border:'1px dashed var(--border)'}}
                    >
                      {uploading ? 'Uploading…' : '+ Add source file(s)'}
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept=".pdf,.docx,.pptx,.xlsx,.txt,.md,application/pdf,text/plain,text/markdown"
                      onChange={onPickFiles}
                      style={{display:'none'}}
                    />
                    {uploadError && <span style={{fontSize:'var(--text-xs)',color:'#A20235'}}>{uploadError}</span>}
                  </div>
                  {sourceFiles.length > 0 && (
                    <div style={{display:'flex',flexDirection:'column',gap:'var(--space-1)'}}>
                      {sourceFiles.map(f => (
                        <div key={f.key} style={{display:'flex',alignItems:'center',gap:'var(--space-2)',padding:'var(--space-2) var(--space-3)',background:'var(--bg-card)',borderRadius:'var(--r-sm)',fontSize:'var(--text-sm)'}}>
                          <span style={{flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={f.name}>{f.name}</span>
                          <span style={{color:'var(--fg-muted)',fontSize:'var(--text-xs)'}}>{(f.size / 1024).toFixed(1)} KB</span>
                          <button
                            type="button"
                            onClick={() => removeFile(f.key)}
                            disabled={busy}
                            style={{all:'unset',cursor:'pointer',color:'var(--fg-muted)',fontSize:'var(--text-md)',lineHeight:1}}
                            title="Remove from this run"
                          >×</button>
                        </div>
                      ))}
                    </div>
                  )}
                  <textarea
                    className="textarea"
                    value={form.sourceMaterial}
                    onChange={e => set('sourceMaterial', e.target.value)}
                    rows={7}
                    placeholder="…or paste source text here."
                  />
                  {/* Optional structured context — the biggest quality lever for
                      analytical / consulting / adoption decks. Collapsed so it
                      never gets in the way of a quick deck. */}
                  <button
                    type="button"
                    onClick={() => setShowContext(v => !v)}
                    style={{all:'unset',cursor:'pointer',fontSize:'var(--text-xs)',color:'var(--fg-muted)',marginTop:4}}
                  >
                    {showContext ? '− Hide added context' : '+ Add context (audience · known facts & sources · constraints)'}
                  </button>
                  {showContext && (
                    <div style={{display:'flex',flexDirection:'column',gap:'var(--space-2)',marginTop:'var(--space-2)'}}>
                      <input
                        className="input"
                        value={form.audience}
                        onChange={e => set('audience', e.target.value)}
                        placeholder="Audience — e.g. the CIO/CTO leadership team of a Danish pension insurer"
                      />
                      <textarea
                        className="textarea"
                        value={form.knownFacts}
                        onChange={e => set('knownFacts', e.target.value)}
                        rows={4}
                        placeholder="Known facts & sources — anything you already know or want the deck grounded on (figures, names, links). The agent labels the rest [verified] / [benchmark] / [illustrative] rather than inventing it."
                      />
                      <textarea
                        className="textarea"
                        value={form.constraints}
                        onChange={e => set('constraints', e.target.value)}
                        rows={3}
                        placeholder="Constraints & scope — what to include or avoid, decisions already made, the one ask the deck drives to."
                      />
                    </div>
                  )}
                </div>
              </IntakeField>
            </div>

            <div className="intake-col">
              <IntakeField label="Length *" hint="Rough guidance only — the agent's first step is a briefing that recommends an exact slide count for the topic, which you can approve or change before slides are drafted.">
                <div style={{display:'flex',flexDirection:'column',gap:8}}>
                  {[
                    { id: 'short',     label: 'Short',     sub: '1–5 slides — one idea, fast read'        },
                    { id: 'medium',    label: 'Medium',    sub: '6–15 slides — the default for most decks' },
                    { id: 'extensive', label: 'Extensive', sub: '16–50 slides — onboarding, training, deep' },
                  ].map(opt => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => set('length', opt.id)}
                      style={{
                        display:'flex',flexDirection:'column',alignItems:'flex-start',gap:2,
                        padding:'10px 12px',borderRadius:8,cursor:'pointer',textAlign:'left',
                        background: form.length === opt.id ? 'var(--orange-tint, rgba(240,130,0,0.08))' : 'var(--bg-card)',
                        border: form.length === opt.id ? '2px solid var(--orange)' : '1px solid var(--border)',
                        color:'inherit',
                      }}
                    >
                      <strong style={{fontSize:14}}>{opt.label}</strong>
                      <span style={{fontSize:11.5,color:'var(--fg-muted)',lineHeight:1.4}}>{opt.sub}</span>
                    </button>
                  ))}
                </div>
              </IntakeField>

              <IntakeField label="Fidelity" hint="How strictly should the agent stick to your source's slide count, story order and wording? You can still adjust everything on the briefing approval screen.">
                <div style={{display:'flex',flexDirection:'column',gap:8}}>
                  {[
                    { id: 'optimize',  label: 'Optimise it',            sub: 'Treat count, structure & wording as starting points' },
                    { id: 'structure', label: 'Keep count + story',     sub: 'Lock the slide count & order; polish the wording' },
                    { id: 'exact',     label: 'Stick to it exactly',    sub: 'Keep count, order & wording verbatim' },
                  ].map(opt => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => set('fidelity', opt.id)}
                      style={{
                        display:'flex',flexDirection:'column',alignItems:'flex-start',gap:2,
                        padding:'10px 12px',borderRadius:8,cursor:'pointer',textAlign:'left',
                        background: form.fidelity === opt.id ? 'var(--orange-tint, rgba(240,130,0,0.08))' : 'var(--bg-card)',
                        border: form.fidelity === opt.id ? '2px solid var(--orange)' : '1px solid var(--border)',
                        color:'inherit',
                      }}
                    >
                      <strong style={{fontSize:14}}>{opt.label}</strong>
                      <span style={{fontSize:11.5,color:'var(--fg-muted)',lineHeight:1.4}}>{opt.sub}</span>
                    </button>
                  ))}
                </div>
              </IntakeField>

              <IntakeField label="Brand variant *" hint="Eve adds the peach gradient + eve wordmark cover.">
                <select
                  className="select"
                  value={form.brandVariant}
                  onChange={e => set('brandVariant', e.target.value)}
                >
                  <option>Eraneos</option>
                  <option>Eve by Eraneos</option>
                </select>
              </IntakeField>

              <IntakeField
                label="Deck visual style"
                hint="ONE style for the whole deck — the cover, section dividers, split slides and every generated image share it (text and data charts stay clean for legibility). Three PHOTO sources: Photo library reuses the Eraneos library · Unsplash searches on-brand stock · AI generates bespoke styled images (pick the style below — photoreal, impressionist, oil, watercolour, …). Two DRAWN styles (no photo): Illustration is thin line-art; Editorial is filled flat-vector scenes (documents, desks, buildings). Works for both slide creators."
              >
                <div style={{display:'flex',gap:6,background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:8,padding:3,flexWrap:'wrap'}}>
                  {[
                    { id: 'brand',        label: 'Photo library',   sub: 'Eraneos photo library'  },
                    { id: 'unsplash',     label: 'Unsplash search', sub: 'On-brand stock photo'   },
                    { id: 'openai',       label: 'AI-generated',    sub: 'gpt-image-1, styled'    },
                    { id: 'illustration', label: 'Illustration',    sub: 'Drawn line-art, no photo' },
                    { id: 'editorial',    label: 'Editorial',       sub: 'Filled flat-vector scenes' },
                  ].map(opt => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => set('imageSource', opt.id)}
                      style={{
                        flex:'1 1 calc(50% - 6px)',display:'flex',flexDirection:'column',alignItems:'flex-start',gap:1,
                        padding:'8px 10px',borderRadius:6,cursor:'pointer',textAlign:'left',
                        background: form.imageSource === opt.id ? 'var(--orange)' : 'transparent',
                        color: form.imageSource === opt.id ? '#fff' : 'inherit',
                        border:'none',
                      }}
                    >
                      <strong style={{fontSize:13}}>{opt.label}</strong>
                      <span style={{fontSize:11,opacity:.8,lineHeight:1.3}}>{opt.sub}</span>
                    </button>
                  ))}
                </div>
                {form.imageSource === 'openai' && (
                  <select
                    className="select"
                    value={form.imageStyle}
                    onChange={e => set('imageStyle', e.target.value)}
                    style={{marginTop:8}}
                  >
                    {IMAGE_STYLE_GROUPS.map(g => (
                      <optgroup key={g} label={g}>
                        {IMAGE_STYLES.filter(s => s.group === g).map(s => (
                          <option key={s.id} value={s.id}>{s.label}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                )}
              </IntakeField>
              {/* Section dividers follow the one Deck-visual-style choice above —
                  no separate control. Derived at submit (see dividerStyle). */}
            </div>
          </div>

          <div style={{display:'flex',gap:'var(--space-2)',justifyContent:'flex-end'}}>
            <button type="button" className="btn-ghost" onClick={onCancel}>Cancel</button>
            <button type="submit" className="btn-orange" disabled={!canSubmit}>
              {busy ? 'Designing deck…' : 'Create deck'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ── PromptTemplateCard ─────────────────────────────────────────────
 *
 * Collapsed by default — clicking the header expands the prompt. When
 * expanded, an "Edit" button swaps to a textarea + Save / Cancel.
 * Saving fires patchAgent({ promptTemplate }) and calls onSaved so
 * the parent re-fetches.
 *
 * The card replaces the old always-on, always-expanded prompt block
 * that was eating the top half of the agent detail page.
 */
const PromptTemplateCard = ({ agent, canEdit, onSaved }) => {
  const [open, setOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(agent.promptTemplate || '');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);

  // Resync the draft if the upstream agent object changes (e.g. after
  // a parent reload). Don't touch it while the user is mid-edit.
  React.useEffect(() => {
    if (!editing) setDraft(agent.promptTemplate || '');
  }, [agent.promptTemplate, editing]);

  const startEdit = () => {
    setError(null);
    setEditing(true);
    setOpen(true);
  };

  const dirty = editing && draft.trim() !== (agent.promptTemplate || '').trim();

  // Auto-save persists the draft without collapsing the editor — the user
  // closes it explicitly with Done. An empty prompt is rejected loudly so it
  // surfaces as a failed-save state rather than silently wiping the template.
  const save = async () => {
    const next = draft.trim();
    if (!next) throw new Error('Prompt cannot be empty.');
    setBusy(true);
    setError(null);
    try {
      await patchAgent(agent.id, { promptTemplate: next });
      onSaved?.();
    } catch (err) {
      setError(err.message || String(err));
      throw err;
    } finally {
      setBusy(false);
    }
  };

  const { status: saveStatus, saveNow } = useAutoSave({
    dirty,
    save,
    enabled: editing,
    signature: draft,
  });

  const done = () => { saveNow(); setEditing(false); setError(null); };

  return (
    <div style={{marginTop:'var(--space-3)',padding:'var(--space-3) var(--space-4)',background:'var(--bg)',borderRadius:'var(--r-md)',border:'1px solid var(--border)'}}>
      <div
        onClick={() => { if (!editing) setOpen(o => !o); }}
        style={{
          display:'flex',alignItems:'center',gap:'var(--space-2)',
          cursor: editing ? 'default' : 'pointer',
          userSelect:'none',
        }}
        role="button"
        tabIndex={editing ? -1 : 0}
        onKeyDown={e => { if (!editing && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setOpen(o => !o); } }}
      >
        <span style={{fontSize:'var(--text-xs)',letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--fg-muted)',fontWeight:600,flex:1}}>
          Prompt template{editing ? ' — editing' : ''}
        </span>
        {!editing && (
          <span style={{fontSize:'var(--text-xs)',color:'var(--fg-muted)'}}>
            {open ? '▾ click to collapse' : '▸ click to expand'}
          </span>
        )}
      </div>

      {open && !editing && (
        <>
          <div style={{
            marginTop:'var(--space-3)',
            fontSize:'var(--text-sm)',
            lineHeight:'var(--lh-relaxed)',
            whiteSpace:'pre-wrap',
            fontFamily:'inherit',
            maxHeight:480,
            overflowY:'auto',
            padding:'var(--space-2) var(--space-3)',
            background:'var(--bg-alt)',
            borderRadius:'var(--r-sm)',
          }}>
            {agent.promptTemplate}
          </div>
          {canEdit && (
            <div style={{marginTop:'var(--space-3)',display:'flex',justifyContent:'flex-end'}}>
              <button type="button" className="btn-ghost" onClick={startEdit}>Edit</button>
            </div>
          )}
        </>
      )}

      {editing && (
        <div style={{marginTop:'var(--space-3)'}}>
          <textarea
            className="textarea"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            rows={18}
            style={{fontFamily:'inherit',fontSize:'var(--text-sm)',lineHeight:'var(--lh-relaxed)'}}
          />
          {error && (
            <div style={{marginTop:'var(--space-2)',fontSize:'var(--text-xs)',color:'#A20235'}}>{error}</div>
          )}
          <div style={{marginTop:'var(--space-3)',display:'flex',gap:'var(--space-2)',justifyContent:'flex-end',alignItems:'center'}}>
            <SaveStatus status={saveStatus} error={error} dirty={dirty}/>
            <button type="button" className="btn-orange" onClick={done} disabled={busy}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
};

/* Run history list. Output-first: the LATEST run sits in a big card
 * with its teaser + artefact link prominent, and its step board
 * auto-expanded so the user lands on what just happened. Older runs
 * are compact rows below, each collapsible to reveal its steps on
 * demand — no auto-expand for older runs (avoids dozens of running
 * pollers, keeps mobile screens short). */
/* One run, rendered as the single unified card used everywhere — an
 * agent's own run history AND the cross-agent timeline. Collapsed: a
 * compact pill row that LEADS WITH THE RUN'S TITLE (its intent), not the
 * generated slide/section text, alongside status, time and an inline CTA.
 * Open: the full RunBoard card itself — one border, with status, time and
 * title shown exactly once and a collapse control in its meta line. No
 * outer wrapper border (so no box-in-box) and no duplicated header.
 * `agentLabel` prefixes the title in the cross-agent timeline so runs from
 * different agents stay tellable apart. */
const RunRow = ({
  r, agentId, open, onToggle, markSeen, reload,
  onDelete = null, agentLabel = null, flashing = false, rowRef = null,
}) => {
  const rid = extractRunId(r.summary);
  const rsc = STATUS_COLOR[r.status] || STATUS_COLOR.null;

  // Inline CTA on the collapsed row: "review" jumps straight to the
  // viewer; any other actionable state expands the row to reveal the real
  // control. Marked seen on use. Hidden on narrow screens (see CSS) so the
  // title always wins the row.
  let cta = null;
  if (r.actionState) {
    const cls = `agent-runs__cta${r.needsAttention ? ' agent-runs__cta--attn' : ''}`;
    if (r.actionState === 'review' && rid) {
      const kind = r.teaserKind === 'memo' ? 'memo' : 'deck';
      cta = (
        <a
          href={`#runs/${encodeURIComponent(rid)}/${kind}`}
          className={cls}
          onClick={(e) => { e.stopPropagation(); markSeen?.(r); }}
        >Open {kind} viewer</a>
      );
    } else {
      cta = (
        <button
          type="button"
          className={cls}
          onClick={(e) => { e.stopPropagation(); onToggle(r, true); }}
        >{ACTION_LABEL[r.actionState] || 'Open'}</button>
      );
    }
  }

  const head = (
    <button
      type="button"
      className={`agent-runs__row-head${r.needsAttention ? ' agent-runs__row-head--attn' : ''}`}
      onClick={() => onToggle(r, !open)}
    >
      <span className="agent-runs__pill" style={{background:rsc.bg, color:rsc.fg}}>{r.status}</span>
      <span style={{fontSize:12, color:'var(--fg-muted)', flexShrink:0}}>{relTime(r.startedAt)}</span>
      {(r.ownerName || r.ownerPhotoUrl) && (
        <Avatar src={r.ownerPhotoUrl} name={r.ownerName} size={20} title={r.ownerName || undefined}/>
      )}
      <span className="agent-runs__row-summary">
        {agentLabel && <span style={{color:'var(--fg-muted)'}}>{agentLabel} · </span>}
        <SpawnedRunSummary summary={r.summary} compact/>
      </span>
      {r.needsAttention && <span className="agent-runs__attn-tag">{ACTION_SHORT[r.actionState] || 'Action needed'}</span>}
      {cta}
      <Icon name={open ? 'collapse' : 'chevron-down'} size={14}/>
    </button>
  );

  return (
    <div ref={rowRef} className={`agent-runs__latest${flashing ? ' agent-runs__flash' : ''}`}>
      {open && rid ? (
        <AgentRunInline
          agentId={agentId}
          runId={rid}
          onRunComplete={reload}
          onCollapse={() => onToggle(r, false)}
          onDelete={onDelete}
        />
      ) : open && !rid ? (
        <div className="agent-runs__row">
          {head}
          <div className="agent-runs__row-body">
            <div style={{padding:10,background:'var(--bg)',border:'1px solid var(--border)',borderRadius:8,fontSize:12,color:'var(--fg-muted)'}}>
              No agent run linked.
            </div>
          </div>
        </div>
      ) : (
        <div className="agent-runs__row">{head}</div>
      )}
    </div>
  );
};

const AgentRunsList = ({ agent, runs, isAdmin, canRun, busy, setBusy, setRunError, reload, focusRunId }) => {
  // Per-row manual open/close, keyed by the row's id. When a row isn't
  // in this map it falls back to the attention-driven default: a run
  // that needs attention (actionable + not opened since its update) or
  // that's still in flight is open; everything settled is collapsed.
  const [openOverride, setOpenOverride] = React.useState({});

  // Deep-link scroll target. A run row's agent-run id is embedded in
  // its summary ("Spawned run <uuid>"); fall back to the row id for
  // rows that are themselves the run.
  const latestRef = React.useRef(null);
  const olderRefs = React.useRef({});
  const focusedRef = React.useRef(null);
  const [flashKey, setFlashKey] = React.useState(null);

  // A run is open by default when it needs attention (actionable + not
  // opened since its update). The latest run also opens while it's in
  // flight, but only the FIRST time the viewer sees it — once opened,
  // it stays collapsed on reload even while the run keeps progressing,
  // so only a genuinely new action item (needsAttention) re-expands it.
  // Older in-flight runs always stay collapsed (avoids many concurrent
  // pollers).
  const defaultOpen = React.useCallback((r) => {
    if (r.needsAttention) return true;
    const isLatest = runs[0] && r.id === runs[0].id;
    return !!isLatest && RUN_IN_FLIGHT(r.status) && !r.hasBeenSeen;
  }, [runs]);
  const isOpen = React.useCallback(
    (r) => (r.id in openOverride ? openOverride[r.id] : defaultOpen(r)),
    [openOverride, defaultOpen],
  );
  // Opening a run records it as "seen" so it drops out of the attention
  // queue (collapses) until the run next updates. Best-effort.
  const markSeen = React.useCallback((r) => {
    const rid = extractRunId(r.summary);
    if (rid) markAgentRunSeen(agent.id, rid);
  }, [agent.id]);
  // Explicit open/close used by the unified RunRow. Marks the run seen
  // when it's opened so it drops out of the attention queue.
  const onToggle = React.useCallback((r, next) => {
    setOpenOverride(s => ({ ...s, [r.id]: next }));
    if (next) markSeen(r);
  }, [markSeen]);

  React.useEffect(() => {
    if (!focusRunId || !runs.length) return;
    if (focusedRef.current === focusRunId) return;   // already scrolled to it
    const idx = runs.findIndex(r => (extractRunId(r.summary) || r.id) === focusRunId);
    if (idx === -1) return;
    focusedRef.current = focusRunId;
    setOpenOverride(s => ({ ...s, [runs[idx].id]: true }));
    setFlashKey(idx === 0 ? 'latest' : runs[idx].id);
    // Let the expand/render settle before scrolling the run into view.
    const scrollT = setTimeout(() => {
      const el = idx === 0 ? latestRef.current : olderRefs.current[runs[idx].id];
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 80);
    const flashT = setTimeout(() => setFlashKey(null), 2200);
    return () => { clearTimeout(scrollT); clearTimeout(flashT); };
  }, [focusRunId, runs]);

  const deleteRun = async (runId) => {
    if (!runId) return;
    if (!window.confirm('Delete this run and any generated artefacts? Comments and slides for this run will also be removed.')) return;
    try {
      setBusy(true);
      await deleteAgentRun(agent.id, runId);
      reload();
    } catch (err) {
      setRunError(`Delete failed: ${err.message || String(err)}`);
    } finally {
      setBusy(false);
    }
  };

  const clearAllRuns = async () => {
    if (!window.confirm(
      'Clear ALL run history for this agent? This permanently deletes every finished run (across all users) and its generated artefacts. In-flight runs are kept. This cannot be undone.',
    )) return;
    try {
      setBusy(true);
      const res = await clearAgentRuns(agent.id);
      setRunError(null);
      reload();
      window.alert(`Cleared ${res.runsDeleted} run${res.runsDeleted === 1 ? '' : 's'}${res.r2Deleted ? ` · ${res.r2Deleted} file${res.r2Deleted === 1 ? '' : 's'} removed` : ''}.`);
    } catch (err) {
      setRunError(`Clear failed: ${err.message || String(err)}`);
    } finally {
      setBusy(false);
    }
  };

  if (runs.length === 0) {
    return (
      <div>
        <div className="agents-section-title">Run history</div>
        <div style={{padding:14,background:'var(--bg)',border:'1px solid var(--border)',borderRadius:10,fontSize:13,color:'var(--fg-muted)'}}>
          No runs yet. {canRun ? 'Click "Run now" above to trigger one.' : 'Waiting for the next scheduled pass.'}
        </div>
      </div>
    );
  }

  const [latest, ...older] = runs;
  const latestRunId = extractRunId(latest.summary);

  // No agent_run linked (reflection / master run) → there's no board to
  // collapse, so keep the summary shown. Otherwise follow the
  // attention/in-flight default + any manual toggle.
  const latestOpen = latestRunId ? isOpen(latest) : true;
  const latestCanDelete = !!latestRunId && latest.status !== 'running' && latest.status !== 'planning';
  const sc = STATUS_COLOR[latest.status] || STATUS_COLOR.null;

  return (
    <div className="agent-runs">
      <div style={{display:'flex',alignItems:'center',gap:8}}>
        <div className="agents-section-title" style={{flex:1}}>Latest output</div>
        {isAdmin && (
          <button
            type="button"
            className="agent-runs__delete-text"
            onClick={clearAllRuns}
            disabled={busy}
            title="Delete all run history for this agent"
          >
            <Icon name="trash" size={12}/> Clear all runs
          </button>
        )}
      </div>

      {latestRunId ? (
        // The living run card IS the latest-output item. RunRow renders it
        // open (full card, collapse control) or collapsed (the pill row),
        // with no box-in-box and the title/status/time shown once.
        <RunRow
          r={latest}
          agentId={agent.id}
          open={latestOpen}
          onToggle={onToggle}
          markSeen={markSeen}
          reload={reload}
          onDelete={latestCanDelete && !busy ? () => deleteRun(latestRunId) : null}
          rowRef={latestRef}
          flashing={flashKey === 'latest'}
        />
      ) : (
        // Reflection summary (no agent_run linked) — keep the compact summary.
        <div
          ref={latestRef}
          className={`agent-runs__latest${flashKey === 'latest' ? ' agent-runs__flash' : ''}`}
        >
          <div className="agent-runs__latest-head">
            <span className="agent-runs__pill" style={{background:sc.bg, color:sc.fg}}>{latest.status}</span>
            <span style={{fontSize:12, color:'var(--fg-muted)'}}>{relTime(latest.startedAt)}</span>
          </div>
          <div className="agent-runs__summary">
            <SpawnedRunSummary summary={latest.summary}/>
          </div>
        </div>
      )}

      {older.length > 0 && (
        <>
          <div className="agents-section-title" style={{marginTop:24}}>Earlier runs · {older.length}</div>
          <div className="agent-runs__older">
            {older.map(r => {
              const rid = extractRunId(r.summary);
              const can = !!rid && r.status !== 'running' && r.status !== 'planning';
              return (
                <RunRow
                  key={r.id}
                  r={r}
                  agentId={agent.id}
                  open={isOpen(r)}
                  onToggle={onToggle}
                  markSeen={markSeen}
                  reload={reload}
                  onDelete={can && !busy ? () => deleteRun(rid) : null}
                  rowRef={el => { olderRefs.current[r.id] = el; }}
                  flashing={flashKey === r.id}
                />
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

/* ── RunsTimeline ───────────────────────────────────────────────────
 * Shown on the right pane when no agent is selected. A chronological
 * feed of the latest runs across every agent in the active selector
 * (all / system / public / personal). Each row leads with the run
 * status + agent it belongs to and a one-line summary; clicking it
 * selects that agent so the full history + output opens. Data is the
 * /api/agents `recentRuns` feed, already scoped to the tab by the
 * caller. */
/**
 * Runs timeline for the selected scope (All / a category). Tops out with a
 * "+ New run" row that opens the agent picker; each run is a timeline entry
 * that opens the run in a focused window. Replaces the old flat feed.
 */
const RunsTimeline = ({ runs, tabLabel, onSelectAgent, onNewRun, reload }) => {
  // Expanded rows are tracked locally; the cross-agent timeline now uses
  // the exact same RunRow as an agent's own history, so a row opens INLINE
  // into the full RunBoard card (preview + buttons) right here.
  const [openMap, setOpenMap] = React.useState({});
  const isOpen = (r) => !!openMap[r.id];
  const markSeen = (r) => {
    const rid = extractRunId(r.summary);
    if (rid) markAgentRunSeen(r.agentId, rid).catch(() => {});
  };
  const onToggle = (r, next) => {
    // No linked agent_run to expand (reflection / master) → fall back to
    // opening the owning agent instead.
    if (next && !extractRunId(r.summary)) { onSelectAgent(r.agentId); return; }
    setOpenMap(prev => ({ ...prev, [r.id]: next }));
    if (next) markSeen(r);
  };
  return (
    <div className="agent-runs">
      <div className="agents-section-title" style={{marginBottom:10}}>
        Recent runs · {tabLabel}{runs.length ? ` · ${runs.length}` : ''}
      </div>

      {/* + New run — pick an agent, then run it */}
      <div
        role="button" tabIndex={0} onClick={onNewRun}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') onNewRun(); }}
        style={{display:'flex',alignItems:'center',gap:12,padding:'13px 15px',border:'1.5px dashed var(--orange)',borderRadius:14,background:'#FFF6F2',cursor:'pointer',marginBottom:14}}
      >
        <span style={{width:30,height:30,borderRadius:'50%',background:'var(--orange)',color:'#fff',display:'inline-flex',alignItems:'center',justifyContent:'center',fontSize:19,flexShrink:0}}>+</span>
        <div style={{minWidth:0}}>
          <div style={{fontSize:14.5,fontWeight:600,color:'var(--orange)'}}>New run</div>
          <div style={{fontSize:12,color:'var(--fg-muted)',marginTop:1}}>Choose an agent to run →</div>
        </div>
      </div>

      {runs.length === 0 ? (
        <div className="agents-detail__empty">
          No runs in the last 36 hours for <strong>{tabLabel}</strong>. Start one above, or pick an agent to see its full history.
        </div>
      ) : (
        <div className="agent-runs__older">
          {runs.map(r => (
            <RunRow
              key={r.id}
              r={r}
              agentId={r.agentId}
              open={isOpen(r)}
              onToggle={onToggle}
              markSeen={markSeen}
              reload={reload}
              agentLabel={r.agentTargetLabel}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/** "+ New run" picker — agents grouped by our categories. Picking one opens
 *  its detail (where Run-now handles any intake form), so no blind runs. */
const AgentPickerModal = ({ agents, onClose, onPick }) => {
  const [q, setQ] = React.useState('');
  const ql = q.trim().toLowerCase();
  const groups = [
    ['Public', agents.filter(a => a.kind === 'custom' && a.scope === 'firm')],
    ['System', agents.filter(a => a.kind === 'system' || a.kind === 'master')],
    ['Personal', agents.filter(a => a.kind === 'custom' && a.scope === 'personal')],
  ];
  return (
    <div className="modal-mask" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{position:'relative',maxWidth:560,width:'95%'}}>
        <button className="modal__close" onClick={onClose}>×</button>
        <div style={{fontSize:11,letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--orange)',marginBottom:6,fontWeight:600}}>New run</div>
        <h3 style={{marginBottom:4}}>Choose an agent</h3>
        <p style={{color:'var(--fg-muted)',fontSize:12.5,marginBottom:12,lineHeight:1.5}}>Pick an agent to open it and start a run.</p>
        <input
          autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search agents…"
          style={{width:'100%',marginBottom:12,padding:'9px 12px',borderRadius:999,border:'1px solid var(--border)',background:'var(--bg-alt)',fontSize:14}}
        />
        <div style={{display:'flex',flexDirection:'column',gap:14}}>
          {groups.map(([label, list]) => {
            const filtered = list.filter(a => !ql || (a.targetLabel || '').toLowerCase().includes(ql));
            if (!filtered.length) return null;
            return (
              <div key={label}>
                <div style={{fontSize:11,letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--fg-muted)',fontWeight:600,marginBottom:6}}>{label} · {filtered.length}</div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                  {filtered.map(a => {
                    const dot = a.lastStatus === 'done' ? '#00702A' : a.lastStatus === 'failed' ? '#A20235' : a.enabled ? '#FF6428' : '#C9BCB0';
                    return (
                      <button
                        key={a.id} type="button" onClick={() => onPick(a)}
                        style={{all:'unset',cursor:'pointer',display:'flex',alignItems:'center',gap:9,padding:'10px 12px',borderRadius:12,border:'1px solid var(--border)'}}
                      >
                        <span style={{width:8,height:8,borderRadius:'50%',background:dot,flexShrink:0}}/>
                        <span style={{minWidth:0}}>
                          <span style={{display:'block',fontSize:13.5,fontWeight:600,lineHeight:1.15,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{a.targetLabel}</span>
                          <span style={{display:'block',fontSize:11,color:'var(--fg-muted)'}}>{a.lastRunAt ? relTime(a.lastRunAt) : 'never run'}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const AgentDetail = ({ id, initialAgent, isAdmin, canRun, onChanged, onEdit, onDelete }) => {
  // Render-instant trick: the list row already carries the agent
  // object (same publicAgent shape as the detail endpoint returns),
  // so seed `data` with it on first render. `runs` starts empty and
  // gets filled when getAgent resolves. Without this seed the right
  // pane flashes "Loading…" on every selection.
  const [data, setData] = React.useState(
    initialAgent ? { agent: initialAgent, runs: [] } : null,
  );
  // Split error state: loadError replaces the whole page (we can't
  // render an agent detail without the agent); runError is shown as
  // an inline banner above the run history so the user keeps context
  // (status pill, run history, controls) while seeing what failed.
  const [loadError, setLoadError] = React.useState(null);
  const [runError, setRunError]   = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  // NOTE: this state used to sit below the early-returns, which is a
  // React-hooks rule violation — the call order changed between the
  // first (loading) render and the second (loaded) render, crashing
  // the page. Always declare every hook at the top.
  const [proposalBusy, setProposalBusy] = React.useState(false);

  // Breadcrumb: eOS › Agents › <agent name>. Seeded from initialAgent so
  // it's right on the first paint, before getAgent resolves.
  const agentLabel = data?.agent?.targetLabel || initialAgent?.targetLabel || '';
  useSubCrumbs(agentLabel ? [{ label: agentLabel, href: null }] : []);

  // Refetch the run history when the firm-wide run scope flips so the
  // detail view follows the "My runs / All runs" toggle in step.
  const runScope = useRunScope();
  const reload = React.useCallback(() => {
    setLoadError(null);
    getAgent(id).then(setData).catch(err => setLoadError(err.message));
  }, [id, runScope]);

  React.useEffect(() => { reload(); }, [reload]);

  const [lastRunMessage, setLastRunMessage] = React.useState(null);
  // Intake modal flow — opens when the user clicks Run on an agent
  // whose id is in INTAKE_MODALS_BY_AGENT_ID. The map's value picks
  // which modal component to render (proposal | redesign | …).
  const [intakeKind, setIntakeKind] = React.useState(null);

  const launchRun = async (inputs) => {
    setBusy(true); setRunError(null); setLastRunMessage(null);
    try {
      // BOTH engines run the SAME multi-step pipeline (briefing + story approval →
      // draft → design slide-by-slide → review → persisted run with comments /
      // share / regenerate / history). This is the helpful, visible process — and
      // designing one slide per request keeps it well under the edge timeout.
      // Slide creator 2 carries engine='mdfirst' so the design step stays on the
      // governed L1/L2 frame + Layer-4 learning. (A one-shot composer that skipped
      // the steps timed out on big decks — do not reintroduce it.)
      const res = await runAgentNow(id, inputs);
      // Custom-agent run: the backend returns `spawnedRun` (the old
      // `spawnedIntent` shape from the intent-system era was renamed
      // in migration 0027 but this branch was never updated, so for
      // months the success message silently failed to appear).
      const spawned = res?.spawnedRun || res?.spawnedIntent;
      if (res?.kind === 'custom' && spawned) {
        const reminderHint = typeof res.remindersScheduled === 'number' && res.remindersScheduled > 0
          ? ` · ${res.remindersScheduled} reminder${res.remindersScheduled === 1 ? '' : 's'} scheduled`
          : '';
        setLastRunMessage(
          res.routerError
            ? `Run ${spawned.id.slice(0, 8)} created but the planner failed: ${res.routerError.slice(0, 200)}`
            : `“${spawned.title}” is running. Watch progress below; the artefact lands in Artifacts when it finishes.${reminderHint}`,
        );
      } else if (res?.run) {
        // Master / reflection run came back. status='failed' means
        // the runtime threw inside runLearningPass; surface the
        // errorMessage prominently rather than only the summary.
        if (res.run.status === 'failed') {
          const msg = res.run.errorMessage || res.run.summary || 'Run failed with no message.';
          setRunError(`Run failed: ${msg}`);
        } else {
          setLastRunMessage(`Run ${res.run.status} · ${res.run.summary || ''}`);
        }
      }
      await reload();
      onChanged?.();
      // Successful intake-driven run: close the modal. Errors keep
      // it open so the user can adjust and retry without re-typing.
      if (inputs) setIntakeKind(null);
    } catch (err) {
      setRunError(err.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  // The button-click handler: agents with an intake form open the
  // modal; everything else runs immediately.
  const runNow = () => {
    const kind = INTAKE_MODALS_BY_AGENT_ID[id];
    if (kind) {
      setRunError(null); setLastRunMessage(null);
      setIntakeKind(kind);
      return;
    }
    launchRun(null);
  };

  const toggle = async () => {
    if (!data) return;
    try {
      await patchAgent(id, { enabled: !data.agent.enabled });
      await reload();
      onChanged?.();
    } catch (err) {
      setRunError(err.message);
    }
  };

  if (loadError) return <div style={{padding:12,background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:13,whiteSpace:'pre-wrap'}}>{loadError}</div>;
  if (!data) return <div style={{padding:24,color:'var(--fg-muted)',fontSize:13}}>Loading…</div>;

  const { agent, runs } = data;

  // Image management (regenerate / upload / delete) lives in the Edit
  // modal (AgentEditorModal), reached via the Edit button.

  // Prefer the most recent unresolved proposal so the action buttons
  // point at the right thing. Fall back to the latest one with a
  // proposal at all (so resolved ones still display after the fact).
  const latestProposal =
    runs.find(r => r.proposedChangesMd && !r.appliedAt && !r.dismissedAt)
    || runs.find(r => r.proposedChangesMd);

  const applyProposal = async () => {
    if (!latestProposal) return;
    setProposalBusy(true);
    try {
      await applyAgentRun(id, latestProposal.id);
      await reload();
      onChanged?.();
    } catch (err) {
      setRunError(err.message);
    } finally {
      setProposalBusy(false);
    }
  };

  const dismissProposal = async () => {
    if (!latestProposal) return;
    if (!confirm('Dismiss this proposal? It stays in the history but won\'t be applied.')) return;
    setProposalBusy(true);
    try {
      await dismissAgentRun(id, latestProposal.id);
      await reload();
      onChanged?.();
    } catch (err) {
      setRunError(err.message);
    } finally {
      setProposalBusy(false);
    }
  };

  return (
    <div style={{display:'flex',flexDirection:'column',gap:14,minHeight:480}}>
      <div style={{padding:14,background:'var(--bg-alt)',borderRadius:10}}>
        <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:6,flexWrap:'wrap'}}>
          <span style={{fontSize:10.5,color:'var(--fg-muted)'}}>
            {isScheduledAgent(agent) ? 'Runs on schedule' : 'Runs only on demand — no cron'}
          </span>
          <span style={{fontSize:11,color:'var(--fg-muted)'}}>· created {relTime(agent.createdAt)}</span>
          <span style={{fontSize:11,color:'var(--fg-muted)'}}>· Last run: {agent.lastRunAt ? relTime(agent.lastRunAt) : '—'}</span>
          <span style={{fontSize:11,color:'var(--fg-muted)'}}>· Next: {nextRunHint(agent)}</span>

          <div style={{flex:1}}/>
          {isAdmin && (
            <>
              {/* On/off toggle, now a labeled button sitting next to Edit. */}
              <button
                type="button"
                onClick={toggle}
                disabled={busy}
                title={isScheduledAgent(agent)
                  ? (agent.enabled ? 'Auto on — scheduled runs happen. Click to turn off.' : 'Auto off — no scheduled runs. Click to turn on.')
                  : (agent.enabled ? 'Available on — people can use it. Click to make unavailable.' : 'Available off — hidden from people. Click to make available.')}
                className="btn-ghost"
                style={{padding:'4px 10px 4px 8px',fontSize:12,display:'inline-flex',alignItems:'center',gap:8,opacity:busy?0.6:1}}
              >
                <span style={{width:30,height:18,borderRadius:9,position:'relative',background:agent.enabled?'var(--orange)':'var(--bg-card)',transition:'background 0.12s',flexShrink:0}}>
                  <span style={{position:'absolute',top:2,left:agent.enabled?14:2,width:14,height:14,borderRadius:'50%',background:'#fff',boxShadow:'0 1px 2px rgba(0,0,0,0.18)',transition:'left 0.12s'}}/>
                </span>
                <span style={{color:agent.enabled?'var(--orange)':'var(--fg-muted)',fontWeight:600}}>{toggleNoun(agent)} {agent.enabled ? 'on' : 'off'}</span>
              </button>
              {onEdit && (
                <button className="btn-ghost" style={{padding:'4px 10px',fontSize:12}} onClick={() => onEdit(agent)} disabled={busy} title="Edit prompt template">
                  Edit
                </button>
              )}
              {onDelete && (
                <button className="btn-ghost" style={{padding:'4px 10px',fontSize:12,color:'#A20235'}} onClick={() => onDelete(agent)} disabled={busy} title="Delete this agent">
                  Delete
                </button>
              )}
            </>
          )}
          {/* Run-now is gated by canRun (mirrors run-now.ts), not isAdmin:
              eOS-Managers and users can spawn a run on any public agent
              shared with their role, and owners on their personal agents —
              without edit/toggle/delete rights. System agents stay
              admin-only and the toggle above is hidden for non-admins. */}
          {canRun && agent.kind !== 'system' && (
            <button className="btn-orange" style={{padding:'4px 12px',fontSize:12}} onClick={runNow} disabled={busy}>
              {busy ? 'Running…' : 'Run now'}
            </button>
          )}
        </div>
        {agent.description && (
          <div style={{fontSize:13.5,marginTop:10,lineHeight:1.55}}>{agent.description}</div>
        )}
        {/* Built-in agents (master watchers + system singletons) have no
            user-editable prompt: their behaviour is defined in code. Say so
            rather than leaving an empty prompt field. */}
        {agent.builtin && (
          <div style={{marginTop:10,padding:'10px 12px',background:'var(--bg)',border:'1px solid var(--border)',borderRadius:8,fontSize:12.5,color:'var(--fg-muted)',lineHeight:1.5}}>
            <span style={{fontWeight:600,color:'var(--fg-subtle)'}}>⚙ Built-in agent — behaviour defined in code</span>
            <div style={{marginTop:4}}>
              {agent.kind === 'master'
                ? 'This watcher uses a firm-wide default “search lens” set in code, so the prompt field reads empty until an admin overrides it. Use Edit to set an override (clear it to track the default again).'
                : 'Its steps are hardcoded in the platform — there is no editable prompt here.'}
            </div>
          </div>
        )}
        {/* Custom agent that somehow has no stored prompt (legacy / seeded). */}
        {agent.kind === 'custom' && !agent.promptTemplate && (
          <div style={{marginTop:10,padding:'10px 12px',background:'var(--bg-alt)',borderRadius:8,fontSize:12.5,color:'var(--fg-muted)'}}>
            No prompt set yet{(isAdmin || agent.scope === 'personal') ? ' — use Edit to add one.' : '.'}
          </div>
        )}
        {lastRunMessage && (
          <div style={{marginTop:10,padding:10,background:'#DCF1E2',color:'#00702A',borderRadius:8,fontSize:12.5,lineHeight:1.5}}>{lastRunMessage}</div>
        )}
        {runError && (
          <div style={{marginTop:10,padding:'10px 12px',background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:12.5,lineHeight:1.5,whiteSpace:'pre-wrap',wordBreak:'break-word',maxHeight:280,overflowY:'auto'}}>
            <div style={{fontSize:11,fontWeight:600,letterSpacing:'0.06em',textTransform:'uppercase',marginBottom:4,opacity:0.7}}>Run failed</div>
            {runError}
          </div>
        )}
      </div>

      {latestProposal && (() => {
        const resolved = latestProposal.appliedAt || latestProposal.dismissedAt;
        const borderColor = latestProposal.appliedAt   ? 'var(--support-green)'
                          : latestProposal.dismissedAt ? 'var(--border)'
                          : 'var(--orange)';
        const headerColor = latestProposal.appliedAt   ? 'var(--support-green)'
                          : latestProposal.dismissedAt ? 'var(--fg-muted)'
                          : 'var(--orange)';
        const headerLabel = latestProposal.appliedAt   ? `Applied · ${relTime(latestProposal.appliedAt)}`
                          : latestProposal.dismissedAt ? `Dismissed · ${relTime(latestProposal.dismissedAt)}`
                          : `Proposed change · ${relTime(latestProposal.startedAt)}`;
        return (
          <div style={{padding:14,border:`2px solid ${borderColor}`,borderRadius:10,background:'var(--bg)'}}>
            <div style={{fontSize:11,letterSpacing:'0.06em',textTransform:'uppercase',color:headerColor,marginBottom:6,fontWeight:600}}>
              {headerLabel}
            </div>
            <pre style={{margin:0,whiteSpace:'pre-wrap',fontFamily:'inherit',fontSize:13,lineHeight:1.5}}>{latestProposal.proposedChangesMd}</pre>
            <div style={{marginTop:10}}>
              <ThumbsRow
                target={{ type: 'agent_run', id: latestProposal.id, label: latestProposal.summary || 'agent run' }}
                snapshots={{
                  intent: 'Agent run produced this proposal',
                  aiDid: latestProposal.proposedChangesMd || latestProposal.summary || '',
                  link: `#agents`,
                }}
                compact
              />
            </div>
            {isAdmin && !resolved && (
              <div style={{display:'flex',gap:8,marginTop:10}}>
                <span style={{fontSize:11,color:'var(--fg-muted)',fontStyle:'italic',alignSelf:'center'}}>
                  Master agents write directly (news ticker, chats). Apply is retired — use Dismiss to clear.
                </span>
                <button className="btn-ghost" style={{padding:'4px 12px',fontSize:12}} onClick={dismissProposal} disabled={proposalBusy}>
                  Dismiss
                </button>
                <div style={{flex:1}}/>
                {false && (
                  <span style={{fontSize:11,color:'var(--fg-muted)',alignSelf:'center'}}>
                    Apply appends this to the entity's description (audit-logged).
                  </span>
                )}
              </div>
            )}
            {resolved && (
              <div style={{fontSize:11,color:'var(--fg-muted)',marginTop:8}}>
                {latestProposal.appliedAt
                  ? 'Applied to the entity\'s description. Edit further in Admin if needed.'
                  : 'Dismissed — no changes were written.'}
              </div>
            )}
          </div>
        );
      })()}

      <AgentRunsList
        agent={agent}
        runs={runs}
        isAdmin={isAdmin}
        canRun={canRun}
        busy={busy}
        setBusy={setBusy}
        setRunError={setRunError}
        reload={reload}
      />

      {intakeKind === 'proposal' && (
        <ProposalIntakeModal
          busy={busy}
          onCancel={() => setIntakeKind(null)}
          onLaunch={(inputs) => { launchRun(inputs); }}
        />
      )}
      {intakeKind === 'redesign' && (
        <RedesignIntakeModal
          busy={busy}
          onCancel={() => setIntakeKind(null)}
          onLaunch={(inputs) => { launchRun(inputs); }}
        />
      )}
      {intakeKind === 'slidedeck' && (
        <SlideDeckIntakeModal
          busy={busy}
          onCancel={() => setIntakeKind(null)}
          onLaunch={(inputs) => { launchRun(inputs); }}
        />
      )}
      {intakeKind === 'markdown' && (
        <MarkdownIntakeModal
          busy={busy}
          onCancel={() => setIntakeKind(null)}
          onLaunch={(inputs) => { launchRun(inputs); }}
        />
      )}
    </div>
  );
};


function relTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  const abs = Math.abs(Date.now() - d.getTime());
  const min = 60_000, hr = 60 * min, day = 24 * hr;
  if (abs < min)     return 'just now';
  if (abs < hr)      return `${Math.round(abs / min)}m ago`;
  if (abs < day)     return `${Math.round(abs / hr)}h ago`;
  if (abs < 7 * day) return `${Math.round(abs / day)}d ago`;
  return d.toLocaleDateString();
}

/**
 * Heuristic "when will this agent run next?" for the left list and
 * detail header. Reflection agents (function / service / industry /
 * client / user) are claimed by the runner Worker when
 * last_run_at < now - 23h. Custom agents are manual-trigger only
 * (no scheduler today), so we just show "manual".
 */
function nextRunHint(a) {
  if (!a.enabled) return 'off';
  // Custom + admin-system agents with a schedule fire on cron; without
  // one they're Run-now only. Master agents fire daily at 01:00 ZRH
  // (the existing master cadence).
  if (a.kind === 'custom' || a.kind === 'system') {
    if (!a.scheduleCron) return 'manual';
    try { return describeCron(a.scheduleCron); }
    catch { return `cron: ${a.scheduleCron}`; }
  }
  const last = a.lastRunAt ? +new Date(a.lastRunAt) : 0;
  if (!last) return 'next tick';
  const dueAt = last + 23 * 3600_000;
  const delta = dueAt - Date.now();
  if (delta <= 0) return 'next tick';
  const hr = 3600_000, day = 24 * hr;
  if (delta < hr)  return `in ${Math.max(1, Math.round(delta / 60_000))}m`;
  if (delta < day) return `in ${Math.round(delta / hr)}h`;
  return new Date(dueAt).toLocaleDateString();
}
